import React from "react";
import { createFileRoute } from "@tanstack/react-router";
import type { ExplorerNotebookId } from "@brewbase/db/types";
import { trpc } from "@/hooks/trpc";
import { useDataSources } from "@/features/fermentation-charts/data-sources";
import { bundleOf } from "@/features/fermentation-charts/core/useBundles";
import {
  generateSeriesColors,
  type FermentationInfo,
  type NonNullColorCode,
} from "@/lib/color-utils";
import ReactECharts, { type EChartsOption } from "echarts-for-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Combobox } from "@/components/ui/combobox";
import { Badge } from "@/components/ui/badge";
import { HtmlContent } from "@/components/ui/html-content";
import { cn } from "@/lib/utils";
import {
  Search,
  RefreshCcw,
  Download,
  Palette,
  List,
  Eye,
  EyeOff,
  PanelRight,
  GripVertical,
  PanelLeft,
  SlidersHorizontal,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import type { FermentationGroupId } from "@brewbase/db/types";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuCheckboxItem,
} from "@/components/ui/dropdown-menu";
import { PyExpressionEditor } from "@/features/py-editor";
import {
  type VariablesData,
  type SeriesPoints,
  type InspectData,
} from "@/services/PyExecutionService";
import {
  useNotebook,
  type NotebookState,
} from "@/features/explorer-notebooks/useNotebook";
import { useTabRuntime } from "@/features/explorer-notebooks/useTabRuntime";
import type {
  Tab,
  Variant as NotebookVariant,
} from "@/features/explorer-notebooks/useNotebook";
import { usePyExecution } from "@/features/explorer-notebooks/usePyExecution";
import {
  Plus,
  Trash2,
  AlertTriangle,
  Loader2,
  X,
  Book,
  FolderPlus,
  Share2,
} from "lucide-react";
import { promptText } from "@/components/global-alert-dialog";
import {
  SlimTabs,
  SlimTabsContent,
  SlimTabsList,
  SlimTabsTrigger,
} from "@/components/ui/slim-tabs";
import { nanoid } from "nanoid";
import { z } from "zod";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

// Utility stats helpers for selection panel
function mean(nums: number[]): number | null {
  if (!nums.length) return null;
  return nums.reduce((a, b) => a + b, 0) / nums.length;
}
function median(nums: number[]): number | null {
  if (!nums.length) return null;
  const arr = nums.slice().sort((a, b) => a - b);
  const mid = Math.floor(arr.length / 2);
  return arr.length % 2 ? arr[mid] : (arr[mid - 1] + arr[mid]) / 2;
}
function sum(nums: number[]): number {
  return nums.reduce((a, b) => a + b, 0);
}
function trapezoidalIntegral(points: [number, number][]): number {
  if (points.length < 2) return 0;
  const pts = points.slice().sort((a, b) => a[0] - b[0]);
  let area = 0;
  for (let i = 1; i < pts.length; i++) {
    const [x0, y0] = pts[i - 1];
    const [x1, y1] = pts[i];
    area += ((y0 + y1) / 2) * (x1 - x0);
  }
  return area;
}

export const Route = createFileRoute("/fermentations/canvas")({
  component: ExplorePage,
  validateSearch: z.object({
    notebook: z.coerce
      .number()
      .transform((n) => n as ExplorerNotebookId)
      .optional(),
    fs: z.coerce.boolean().optional(),
    dv: z.string().optional(),
  }),
});

// Compact, dense sizing tokens
const DENSE = {
  textSm: "text-sm leading-4",
  chip: "text-xs leading-3 h-5 px-2",
  gap1: "gap-1",
  gap2: "gap-2",
  padX: "px-2",
  padY: "py-1",
  border: "border border-olive-6",
};

// Variable bundle shape
type Variant = NotebookVariant;
interface VariableBundle {
  key: string; // base key, e.g. "temperature"
  displayName: string;
  unit?: string | null;
  variants: Partial<Record<Variant, string>>; // map to datasource keys
  category?: string | null;
  hasData: boolean;
}

// # State management
// - Persisted state: user choices that must survive reloads (tabs, selections, visibilities, UI prefs).
// - Runtime state: ephemeral, computed per tab (derived series, errors, pending flags, current selection).

// ### What persists (DB-backed)
// - Notebook JSON lives in `explorer_notebook.state` and is accessed via `useNotebook(selectedNotebookId)`.
// - Shape (simplified):
//   - `selectedGroups: number[]`
//   - `colorMode: "byFermentation" | "byVariable"`
//   - `showRight`, `toolbarState`, `toolbarPos`, `search`
//   - `activeTabId: string`
//   - `tabs: Tab[]`
//     - `id`, `title`
//     - `selectedVariants: { key, variant }[]`
//     - `fermVisibility: Record<string, boolean>`
//     - `fineVisibility: Record<string, boolean>`
//     - `derivedVisibility: Record<string, boolean>`
//   - `derived.variables: Record<string, { name, expression }>` (no visibility here)

// - `useNotebook` deep-merges the raw server state with a `defaultState` so:
//   - `tabs` always exists (defaults to a single "Tab 1").
//   - `activeTabId` defaults to `"tab-1"` if missing.
//   - `derived.variables` is safely merged.

// - Writes are done via `updateState((prev) => next)` which:
//   - Applies an optimistic cache update to the relevant tRPC query result.
//   - Calls `explorerNotebooks.updateState` to persist.
//   - Invalidates and refetches on settle.

// ### What is runtime-only (in-memory)
// - Handled by `useTabRuntime(notebookId, tabIds)` in `ExplorePage`.
// - Per-tab runtime:
//   - `selectedDerived: string | null`
//   - `derivedSeries: Record<string, SeriesPoints>`
//   - `derivedErrors: Record<string, string | null>`
//   - `pending: Record<string, boolean>`
// - API:
//   - `get(tabId)` → returns runtime (always present)
//   - `update(tabId, updater)` → immutable update
//   - `resetTab(tabId)` → clears runtime for that tab
// - Runtime data never touches the DB.

// ### How user actions update state
// - Add/remove variable variant:
//   - Uses `setActiveTab(updater)` which calls `updateState` to persist `tabs[n].selectedVariants`.
// - Toggle series visibility:
//   - Fermentation-level (`fermVisibility`) and per-series (`fineVisibility`) → persisted via `updateState`.
//   - Derived variable visibility (`derivedVisibility`) → persisted via `updateState`.
// - Edit derived expression:
//   - Update `derived.variables[name].expression` via `updateState`.
//   - Immediately re-evaluate across tabs using runtime updates.
// - Evaluate derived:
//   - `evalOneDerivedForTab`/`evalAllDerivedForTab` set `pending` (runtime), run `pyExec.evalExpression`, then write `derivedSeries` and `derivedErrors` (runtime). No DB writes.
// - Select derived entry in the panel:
//   - Updates `runtime.selectedDerived` only.

// ### Render pipeline (what reads what)
// - Chart (`chartOption`):
//   - Reads persisted: `tabs[n].selectedVariants`, visibilities, color mode.
//   - Reads runtime: `runtime.get(tabId).derivedSeries` for derived plots.
// - Legend:
//   - Reads persisted: visibilities.
//   - Reads runtime: `derivedSeries` to list visible derived series.
// - Derived panel list:
//   - Derived defs from `derived.variables`.
//   - Visibility from `tabs[n].derivedVisibility` (persisted).
//   - Error and pending from runtime (`derivedErrors`, `pending`).
//   - Editor value from `derived.variables[name].expression`.

// ### Default tab creation and safety
// - New notebook (or one with `{}` in DB) is merged with defaults, so `tabs` is always an array with a single `"tab-1"`.
// - This fixed the error where `prev.tabs` could be undefined on first change.

// ### Quick reference
// - Persisted (via `updateState`):
//   - `tabs[n].selectedVariants`, `tabs[n].fermVisibility`, `tabs[n].fineVisibility`, `tabs[n].derivedVisibility`
//   - `derived.variables[name].expression`
//   - UI prefs: `activeTabId`, `colorMode`, `showRight`, `toolbarState`, `toolbarPos`, `search`, `selectedGroups`
// - Runtime (via `useTabRuntime`):
//   - `selectedDerived`, `derivedSeries`, `derivedErrors`, `pending`

// - Default handling:
//   - `useNotebook` deep-merges raw state with `defaultState` to ensure `tabs` and `activeTabId` exist.

// - Optimistic updates:
//   - `useNotebook` performs optimistic cache writes on mutate and invalidates after server completion, keeping the UI responsive.

// - Debounced derived eval:
//   - `evalOneDerivedForTab` debounces 250ms per `tabId:name` key to avoid rapid recompute; writes runtime only.

// - Active tab workflow:
//   - `activeTabId` persisted; runtime store is keyed off current tab ids and notebook id, so new tabs initialize cleanly and closed tabs' runtime is pruned automatically.

function ExplorePage() {
  const routeSearch = Route.useSearch();
  const navigate = Route.useNavigate();
  const selectedNotebookId = routeSearch?.notebook ?? null;

  const { state: nbState, updateState } = useNotebook(selectedNotebookId);
  // Select multiple fermentation groups
  const { data: groups } = trpc.fermentations.list.useQuery();
  // Build global fermentation list and grouping
  const allFermentations = React.useMemo(
    () =>
      (groups ?? []).map((f: any) => ({
        id: f.id as number,
        name: (f.name as string) ?? `F-${String(f.id)}`,
        group_id: f.group_id as FermentationGroupId,
        group_name: (f.group_name as string) ?? String(f.group_id),
      })),
    [groups],
  );
  const fermsByGroup = React.useMemo(() => {
    const m = new Map<number, { id: number; name: string }[]>();
    for (const f of allFermentations) {
      if (!m.has(f.group_id)) m.set(f.group_id, []);
      m.get(f.group_id)!.push({ id: f.id, name: f.name });
    }
    for (const v of m.values()) v.sort((a, b) => a.name.localeCompare(b.name));
    return Object.fromEntries(m.entries()) as Record<
      number,
      { id: number; name: string }[]
    >;
  }, [allFermentations]);
  const groupNameById = React.useMemo(() => {
    const m = new Map<number, string>();
    for (const f of allFermentations)
      if (!m.has(f.group_id)) m.set(f.group_id, f.group_name);
    return Object.fromEntries(m.entries()) as Record<number, string>;
  }, [allFermentations]);
  const availableGroups = React.useMemo(() => {
    const map = new Map<
      number,
      { group_id: FermentationGroupId; group_name: string }
    >();
    (groups ?? []).forEach((f: any) => {
      if (!map.has(f.group_id)) {
        map.set(f.group_id, {
          group_id: f.group_id as FermentationGroupId,
          group_name: f.group_name ?? String(f.group_id),
        });
      }
    });
    return Array.from(map.values());
  }, [groups]);

  const selectedGroups =
    (nbState?.selectedGroups as unknown as FermentationGroupId[]) ?? [];
  const setSelectedGroups: React.Dispatch<
    React.SetStateAction<FermentationGroupId[]>
  > = (updater) => {
    updateState((prev) => {
      const next =
        typeof updater === "function"
          ? updater(prev.selectedGroups as FermentationGroupId[])
          : updater;
      return { ...prev, selectedGroups: next };
    });
  };

  // Fetch data via existing sources
  const { data } = useDataSources(selectedGroups);

  // Derived variable definitions (persisted)
  const derivedDefs = nbState?.derived?.variables ?? {};

  // Tabs state (persisted in notebook)
  const tabs: Tab[] = React.useMemo(() => {
    const persisted = (nbState?.tabs || []) as Tab[];
    if (persisted.length > 0) return persisted;
    // Fallback (not yet persisted); treated as read-only until first write
    return [
      {
        id: "tab-1",
        title: "Tab 1",
        selectedVariants: [],
        fermVisibility: {},
        derivedVisibility: {},
        fineVisibility: {},
      },
    ];
  }, [nbState]);
  const activeTabId: string = React.useMemo(
    () => (nbState?.activeTabId as string) || tabs[0].id,
    [nbState, tabs],
  );
  const activeTab = React.useMemo(
    () => tabs.find((t) => t.id === activeTabId) || tabs[0],
    [tabs, activeTabId],
  );
  // Runtime state for ephemeral per-tab data
  const runtime = useTabRuntime(
    selectedNotebookId,
    tabs.map((t) => t.id),
  );
  // Tab renaming state
  const [renamingTabId, setRenamingTabId] = React.useState<string | null>(null);
  const [tabTitleDraft, setTabTitleDraft] = React.useState<string>("");
  const startRename = React.useCallback((id: string, currentTitle: string) => {
    setRenamingTabId(id);
    setTabTitleDraft(currentTitle);
  }, []);
  const commitRename = React.useCallback(() => {
    if (!renamingTabId) return;
    const title = tabTitleDraft.trim();
    if (title) {
      updateState((prev) => ({
        ...prev,
        tabs: (prev?.tabs ?? []).map((t) =>
          t.id === renamingTabId ? { ...t, title } : t,
        ),
      }));
    }
    setRenamingTabId(null);
    setTabTitleDraft("");
  }, [renamingTabId, tabTitleDraft, updateState]);
  const cancelRename = React.useCallback(() => {
    setRenamingTabId(null);
    setTabTitleDraft("");
  }, []);
  const setActiveTab = React.useCallback(
    (updater: (t: Tab) => Tab) => {
      updateState((prev) => ({
        ...prev,
        tabs: prev.tabs.map((t) => (t.id === activeTab.id ? updater(t) : t)),
      }));
    },
    [activeTab.id, updateState],
  );
  const addTab = React.useCallback(() => {
    const newId = nanoid();
    updateState((prev) => {
      const nextIndex = (prev.tabs?.length ?? 0) + 1;
      const t: Tab = {
        id: newId,
        title: `Tab ${nextIndex}`,
        selectedVariants: [],
        fermVisibility: {},
        derivedVisibility: {},
        fineVisibility: {},
      };
      return {
        ...prev,
        tabs: [...(prev?.tabs ?? []), t],
        activeTabId: newId,
        toolbarState: "all",
      };
    });
    setRenamingTabId(newId);
  }, [updateState]);
  const closeTab = React.useCallback(
    (id: string) => {
      updateState((prev) => {
        const arr: Tab[] = prev.tabs;
        if (!arr || arr.length <= 1) return prev;
        const idx = arr.findIndex((t) => t.id === id);
        const filtered = arr.filter((t) => t.id !== id);
        const nextActive =
          prev.activeTabId === id
            ? filtered[Math.max(0, idx - 1)]?.id
            : prev.activeTabId;
        return { ...prev, tabs: filtered, activeTabId: nextActive };
      });
    },
    [activeTabId, updateState],
  );

  // Build base variables payload for Pyodide (VariablesData)
  const baseVariables = React.useMemo<VariablesData>(() => {
    const online = (data?.["fermentation-online"] ?? {}) as VariablesData;
    return online || {};
  }, [data]);

  // Centralized Python execution lifecycle
  const py = usePyExecution(baseVariables);
  // Sync fullscreen editor state with URL (fs) and selected derived (dv)

  // Auto-evaluate derived variables on load and when inputs change
  const derivedDefList = React.useMemo(
    () =>
      Object.values(
        (derivedDefs ?? {}) as Record<
          string,
          { name: string; expression: string }
        >,
      ),
    [derivedDefs],
  );
  const baseVarsKey = React.useMemo(() => {
    try {
      return JSON.stringify(baseVariables ?? null);
    } catch {
      return null;
    }
  }, [baseVariables]);
  const lastWarmupKeyRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (!py.ready || py.ingesting) return;
    if (!baseVarsKey) return;
    if (derivedDefList.length === 0) return;
    const defsKey = JSON.stringify(
      derivedDefList.map((d) => [d.name, d.expression] as [string, string]),
    );
    const key = `${baseVarsKey}|${defsKey}`;
    if (lastWarmupKeyRef.current === key) return;
    const missing = derivedDefList.filter(
      (d) => !(d.name in (py.results || {})),
    );
    const toEval = missing.length > 0 ? missing : derivedDefList;
    void py.evalMany(toEval);
    lastWarmupKeyRef.current = key;
  }, [py.ready, py.ingesting, baseVarsKey, derivedDefList]);

  // Build fermentation info for colors
  const fermentationsQuery = trpc.useQueries((t) =>
    (selectedGroups || []).map((groupId) =>
      t.fermentations.list({ groupId: groupId as FermentationGroupId }),
    ),
  );

  // Build data availability summaries per fermentation (selected groups only)
  // Timeseries recordings and points
  const tsGroupQueries = trpc.useQueries((t) =>
    (selectedGroups || []).map((groupId) =>
      t.fermentations.getTimeseriesRecordings({ groupId }),
    ),
  );
  // Offline samples
  const samplesGroupQueries = trpc.useQueries((t) =>
    (selectedGroups || []).map((groupId) =>
      t.fermentations.samples.listByGroup(groupId),
    ),
  );
  type FermDataSummary = {
    tsRecordings: number;
    tsPoints: number;
    offlineSamples: number;
    lastSampleAt: string | null;
  };
  const fermDataSummaryByGroup = React.useMemo(() => {
    const result: Record<number, Record<number, FermDataSummary>> = {};
    // Timeseries
    (selectedGroups || []).forEach((groupId, idx) => {
      const recs = (tsGroupQueries[idx] as any)?.data?.recordings as
        | Array<{
            fermentation_id: number;
            has_data: boolean;
            data_points: number;
          }>
        | undefined;
      if (!result[groupId]) result[groupId] = {};
      (recs || []).forEach((r) => {
        const cur = result[groupId][r.fermentation_id] || {
          tsRecordings: 0,
          tsPoints: 0,
          offlineSamples: 0,
          lastSampleAt: null,
        };
        if (r.has_data) {
          cur.tsRecordings += 1;
          cur.tsPoints += Number(r.data_points || 0);
        }
        result[groupId][r.fermentation_id] = cur;
      });
    });
    // Offline samples
    (selectedGroups || []).forEach((groupId, idx) => {
      const samples = (samplesGroupQueries[idx] as any)?.data as
        | Array<{ fermentation_id: number | null; date_time: string | Date }>
        | undefined;
      (samples || []).forEach((s) => {
        if (s.fermentation_id == null) return;
        const cur = result[groupId]?.[s.fermentation_id] || {
          tsRecordings: 0,
          tsPoints: 0,
          offlineSamples: 0,
          lastSampleAt: null,
        };
        cur.offlineSamples += 1;
        const dt =
          typeof s.date_time === "string"
            ? s.date_time
            : s.date_time?.toString?.();
        if (dt) {
          if (!cur.lastSampleAt || new Date(dt) > new Date(cur.lastSampleAt)) {
            cur.lastSampleAt = new Date(dt).toISOString();
          }
        }
        if (!result[groupId]) result[groupId] = {};
        result[groupId][s.fermentation_id] = cur;
      });
    });
    return result;
  }, [selectedGroups, tsGroupQueries, samplesGroupQueries]);

  const fermentations: FermentationInfo[] = React.useMemo(() => {
    const results: FermentationInfo[] = [];
    fermentationsQuery?.forEach((q: any) => {
      q.data?.forEach((f: any) => {
        results.push({
          id: f.id,
          name: f.name ?? `F-${f.id}`,
          color: (f.color_code ?? null) as NonNullColorCode | null,
        });
      });
    });
    return results;
  }, [fermentationsQuery]);

  const seriesColorMap = React.useMemo(
    () => generateSeriesColors(fermentations),
    [fermentations],
  );

  // Sidebar: search and filters
  const search = (nbState?.search as string) ?? "";
  const setSearch = (v: string) =>
    updateState((prev) => ({ ...prev, search: v }));

  // Selection state is per-tab (in TabState)

  // Color mode: by fermentation (default) or by variable
  type ColorMode = "byFermentation" | "byVariable";
  const colorMode = (nbState?.colorMode as ColorMode) ?? "byFermentation";
  const setColorMode = (m: ColorMode) =>
    updateState((prev) => ({ ...prev, colorMode: m }));

  // Group selection mode (persisted)
  type GroupMode = "single" | "compare" | "filter";
  const groupMode: GroupMode = (nbState?.groupMode as GroupMode) ?? "compare";
  const setGroupMode = (m: GroupMode) =>
    updateState((prev) => ({ ...prev, groupMode: m }));
  const singleSelection = (nbState?.singleSelection as
    | { groupId: number | null; fermentationId: number | null }
    | undefined) ?? { groupId: null, fermentationId: null };
  const setSingleSelection = (
    groupId: number | null,
    fermentationId: number | null,
  ) =>
    updateState((prev) => ({
      ...prev,
      singleSelection: { groupId, fermentationId },
    }));

  // Right sidebar visibility
  const showRight = (nbState?.showRight as boolean) ?? false;
  const setShowRight = (on: boolean) =>
    updateState((prev) => ({ ...prev, showRight: on }));

  // Floating toolbar expansion state and draggable position
  type ToolbarState = "buttons" | "legend" | "all";
  const toolbarState = nbState?.toolbarState ?? "buttons";
  const setToolbarState = (s: any) =>
    updateState((prev) => ({ ...prev, toolbarState: s }));
  const toolbarPos = (nbState?.toolbarPos as { x: number; y: number }) ?? {
    x: 16,
    y: 16,
  };
  const [isDraggingToolbar, setIsDraggingToolbar] = React.useState(false);
  const dragOffsetRef = React.useRef<{ dx: number; dy: number }>({
    dx: 0,
    dy: 0,
  });
  const toolbarElRef = React.useRef<HTMLDivElement>(null);
  const lastDragPosRef = React.useRef<{ x: number; y: number } | null>(null);
  const rafIdRef = React.useRef<number | null>(null);
  const onToolbarPointerDown = (e: React.PointerEvent) => {
    if (toolbarState === "all") return; // not draggable when docked
    setIsDraggingToolbar(true);
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    dragOffsetRef.current = {
      dx: e.clientX - toolbarPos.x,
      dy: e.clientY - toolbarPos.y,
    };
  };
  const onToolbarPointerMove = (e: React.PointerEvent) => {
    if (!isDraggingToolbar || toolbarState === "all") return;
    const nextX = Math.max(8, e.clientX - dragOffsetRef.current.dx);
    const minTop = tabsTopOffset + 8;
    const nextY = Math.max(minTop, e.clientY - dragOffsetRef.current.dy);
    lastDragPosRef.current = { x: nextX, y: nextY };
    if (rafIdRef.current == null) {
      rafIdRef.current = window.requestAnimationFrame(() => {
        const el = toolbarElRef.current;
        const pos = lastDragPosRef.current;
        if (el && pos) {
          el.style.left = `${pos.x}px`;
          el.style.top = `${pos.y}px`;
        }
        rafIdRef.current = null;
      });
    }
  };
  const onToolbarPointerUp = (e: React.PointerEvent) => {
    if (!isDraggingToolbar) return;
    setIsDraggingToolbar(false);
    try {
      (e.target as HTMLElement).releasePointerCapture(e.pointerId);
    } catch {}
    if (lastDragPosRef.current) {
      updateState((prev) => ({
        ...prev,
        toolbarPos: lastDragPosRef.current ?? prev.toolbarPos,
      }));
    }
    if (rafIdRef.current != null) {
      try {
        cancelAnimationFrame(rafIdRef.current);
      } catch {}
      rafIdRef.current = null;
    }
  };

  // Build bundles from available attributes API
  const { data: availableAttrs } =
    trpc.fermentationData.getAvailableAttributes.useQuery(
      { fermentationGroupIds: selectedGroups ?? [] },
      { enabled: selectedGroups.length > 0 },
    );

  // Build a stable distinct color for each variable id (e.g. temp_pv)
  const variableColorMap = React.useMemo(() => {
    // Distinct categorical palette (accessible, high-contrast)
    const PALETTE: string[] = [
      // Okabe–Ito (colorblind-safe)
      "#0072B2", // blue
      "#E69F00", // orange
      "#009E73", // green
      "#D55E00", // vermillion
      "#CC79A7", // magenta
      "#F0E442", // yellow
      "#56B4E9", // sky blue
      "#000000", // black

      // Seaborn "deep" family
      "#4C72B0", // deep blue
      "#55A868", // deep green
      "#C44E52", // deep red
      "#8172B2", // deep purple
      "#CCB974", // deep yellow-brown
      "#64B5CD", // deep cyan

      // A few tab10 complements (widely used with seaborn defaults)
      "#1F77B4", // blue
      "#FF7F0E", // orange
      "#2CA02C", // green
      "#9467BD", // purple
      "#8C564B", // brown
      "#17BECF", // cyan
    ];

    const hashString = (s: string) => {
      let h = 0;
      for (let i = 0; i < s.length; i++) {
        h = (h << 5) - h + s.charCodeAt(i);
        h |= 0;
      }
      return Math.abs(h);
    };

    const map: Record<string, string> = {};
    const baseIds = (availableAttrs?.map((a) => a.name) ?? []).slice();
    const derivedIds = Object.keys(derivedDefs ?? {});
    const ids = Array.from(new Set([...baseIds, ...derivedIds])).sort();
    for (const id of ids) {
      const idx = hashString(id) % PALETTE.length;
      map[id] = PALETTE[idx];
    }
    return map;
  }, [availableAttrs, derivedDefs]);

  const bundlesByKey = React.useMemo<Record<string, VariableBundle>>(() => {
    if (!availableAttrs) return {};
    const byBase = new Map<string, VariableBundle>();
    for (const a of availableAttrs) {
      const name = a.name; // e.g. temp_pv, temp_sp, temp_out
      const { base, variant: variantKey } = bundleOf(name);
      if (!byBase.has(base)) {
        byBase.set(base, {
          key: base,
          displayName: a.display_name || base,
          unit: a.unit,
          category: a.category,
          variants: {},
          hasData: true,
        });
      }
      const b = byBase.get(base)!;
      b.variants[variantKey] = name;
      b.hasData = true;
    }
    return Object.fromEntries(byBase.entries());
  }, [availableAttrs]);

  const bundles = React.useMemo<VariableBundle[]>(() => {
    const all = Object.values(bundlesByKey);
    if (all.length === 0) return [];
    const filtered = all.filter((b) => {
      if (!search) return true;
      const s = search.toLowerCase();
      return (
        b.displayName.toLowerCase().includes(s) ||
        b.key.toLowerCase().includes(s) ||
        (b.category ?? "").toLowerCase().includes(s)
      );
    });
    filtered.sort(
      (a, b) =>
        (a.category ?? "").localeCompare(b.category ?? "") ||
        a.displayName.localeCompare(b.displayName),
    );
    return filtered;
  }, [bundlesByKey, search]);

  const toggleVariant = (b: VariableBundle, v: Variant) => {
    const id = { key: b.key, variant: v };
    setActiveTab((t) => {
      const exists = t.selectedVariants.find(
        (p) => p.key === id.key && p.variant === id.variant,
      );
      const selectedVariants = exists
        ? t.selectedVariants.filter(
            (p) => !(p.key === id.key && p.variant === id.variant),
          )
        : [...t.selectedVariants, id];
      return { ...t, selectedVariants };
    });
  };

  // Build ECharts option for central full-bleed plot (enhanced like CombinedChart)
  const chartOption: EChartsOption = React.useMemo(() => {
    const series: any[] = [];
    const legend: string[] = [];
    const safeDerivedSeries = (py.results ?? {}) as Record<
      string,
      SeriesPoints
    >;
    const safeFermVisibility = (activeTab.fermVisibility ?? {}) as Record<
      string,
      boolean
    >;
    const safeFineVisibility = (activeTab.fineVisibility ?? {}) as Record<
      string,
      boolean
    >;

    // Determine which variables (base keys) get their own Y-axes (right side)
    const selectedVariableKeys: string[] = Array.from(
      new Set(activeTab.selectedVariants.map((s) => s.key)),
    );
    const visibleDerivedKeys: string[] = Object.keys(safeDerivedSeries).sort();
    const axisKeysOrdered: string[] = Array.from(
      new Set([...selectedVariableKeys, ...visibleDerivedKeys]),
    );

    // Limit to 6 axes; overflow shares the 6th axis
    const axisKeysLimited = axisKeysOrdered.slice(0, 6);
    const axisIndexByKey: Record<string, number> = {};
    axisKeysOrdered.forEach((k, i) => {
      axisIndexByKey[k] = i < 6 ? i : 5;
    });

    const getAxisMetaForKey = (key: string) => {
      // For data variables, prefer the first available variant id to color the axis
      const bundle = bundlesByKey[key];
      const neutral = "#555";
      if (bundle) {
        const axisName = bundle.unit
          ? `${bundle.displayName} [${bundle.unit}]`
          : bundle.displayName;
        const varId =
          bundle.variants.value ||
          bundle.variants.setpoint ||
          bundle.variants.output ||
          key;
        const axisColor =
          colorMode === "byVariable"
            ? variableColorMap[varId] || neutral
            : neutral;
        return { axisName, axisColor };
      }
      // Derived variable axis
      const axisName = key;
      const axisColor =
        colorMode === "byVariable" ? variableColorMap[key] || neutral : neutral;
      return { axisName, axisColor };
    };

    const yAxes = axisKeysLimited.map((k, idx) => {
      const { axisName, axisColor } = getAxisMetaForKey(k);
      return {
        type: "value",
        name: axisName,
        nameLocation: "middle",
        nameGap: 60,
        nameTextStyle: {
          align: "center",
          verticalAlign: "middle",
          rotate: 90,
          padding: [0, 0, 0, 10],
          color: axisColor,
        },
        position: "right",
        offset: idx * 80,
        axisLabel: { color: axisColor },
        axisLine: { lineStyle: { color: axisColor } },
        axisTick: { lineStyle: { color: axisColor } },
      };
    });

    if (data) {
      for (const sel of activeTab.selectedVariants) {
        // Variant maps to a chartId within data source "fermentation-online" by default
        const chartId = bundlesByKey[sel.key]?.variants[sel.variant];
        if (!chartId) continue;

        const chartData = data["fermentation-online"]?.[chartId];
        if (!chartData) continue;

        Object.entries(chartData).forEach(([seriesName, points]) => {
          const seriesKey = `${seriesName}::${chartId}`;
          if (safeFineVisibility[seriesKey] === false) return;
          if (safeFermVisibility[seriesName] === false) return;
          const color =
            colorMode === "byVariable"
              ? variableColorMap[chartId] || "#888"
              : seriesColorMap[seriesName] || "#888";
          const isSetpoint = sel.variant === "setpoint";
          const isOutput = sel.variant === "output";

          series.push({
            name: `${seriesName}`,
            type: "line",
            showSymbol: false,
            symbol: "none",
            sampling: "lttb",
            progressive: 4000,
            progressiveThreshold: 10000,
            progressiveChunkMode: "mod",
            emphasis: { disabled: true },
            hoverAnimation: false,
            animation: false,
            clip: true,
            lineStyle: {
              width: isSetpoint ? 1 : isOutput ? 1 : 1.2,
              type: isSetpoint ? "dashed" : "solid",
              opacity: isOutput ? 0.6 : 1,
              color,
            },
            color,
            yAxisIndex: axisIndexByKey[sel.key] ?? 0,
            data: points,
          });
          legend.push(`${seriesName}`);
        });
      }
    }

    // Append derived series (visible ones)
    if (derivedDefs) {
      Object.entries(safeDerivedSeries).forEach(([dvName, seriesMap]) => {
        Object.entries(seriesMap).forEach(([seriesName, points]) => {
          const seriesKey = `${seriesName}::${dvName}`;
          if (safeFineVisibility[seriesKey] === false) return;
          if (safeFermVisibility[seriesName] === false) return;
          const color =
            colorMode === "byVariable"
              ? variableColorMap[dvName] || "#555"
              : seriesColorMap[seriesName] || "#555";
          series.push({
            name: `${seriesName}`,
            type: "line",
            showSymbol: false,
            symbol: "none",
            sampling: "lttb",
            progressive: 4000,
            progressiveThreshold: 10000,
            progressiveChunkMode: "mod",
            emphasis: { disabled: true },
            hoverAnimation: false,
            animation: false,
            clip: true,
            lineStyle: {
              width: 1.4,
              type: "solid",
              opacity: 1,
              color,
            },
            color,
            yAxisIndex: axisIndexByKey[dvName] ?? 0,
            data: points,
          });
          legend.push(`${seriesName}`);
        });
      });
    }

    const rightPadding = yAxes.length > 0 ? yAxes.length * 80 : 80;

    return {
      title: { show: false },
      grid: {
        left: 10,
        right: rightPadding,
        top: 10,
        bottom: 40,
        containLabel: false,
      },
      animation: false,
      tooltip: {
        show: true,
        formatter: function (params: any) {
          return null;
        },
        trigger: "axis",
        confine: true,
        axisPointer: { show: true, type: "cross" },
      },
      axisPointer: {
        show: true,
        type: "cross",
        animation: false,
        snap: true,
        label: { show: false },
        link: [{ xAxisIndex: "all", yAxisIndex: "all" }],
      },
      // Replace with custom legend UI; hide ECharts legend
      legend: { show: false },
      xAxis: {
        type: "value",
        name: "EFT [h]",
        nameLocation: "end",
        nameGap: 30,
        axisPointer: { show: true, snap: true },
      },
      yAxis: yAxes.length > 0 ? yAxes : [{ type: "value", position: "right" }],
      series,
      dataZoom: [
        { type: "inside", filterMode: "filter", xAxisIndex: 0 },
        {
          type: "slider",
          filterMode: "filter",
          xAxisIndex: 0,
          height: 18,
          bottom: 8,
          showDetail: false,
        },
        // Enable inside zoom on all Y-axes so rectangle zoom affects Y when present
        ...yAxes.map((_, yi) => ({
          type: "inside" as const,
          filterMode: "filter" as const,
          yAxisIndex: yi,
        })),
      ],
      // Replace with custom toolbar UI; disable ECharts toolbox
      toolbox: { show: false },
      brush: {
        xAxisIndex: "all",
        brushLink: "all",
        outOfBrush: { colorAlpha: 0.1 },
        // Ensure all series are brushable
        seriesIndex: "all",
      },
    } as EChartsOption;
  }, [
    data,
    derivedDefs,
    py.results,
    activeTab.id,
    activeTab.selectedVariants,
    activeTab.fineVisibility,
    bundles,
    seriesColorMap,
    colorMode,
    variableColorMap,
    activeTab.fermVisibility,
  ]);

  const chartRef = React.useRef<ReactECharts>(null);
  const [contextMenuOpen, setContextMenuOpen] = React.useState(false);
  const [contextMenuPos, setContextMenuPos] = React.useState<{
    x: number;
    y: number;
  }>({ x: 0, y: 0 });
  const zoomOnNextBrushRef = React.useRef<boolean>(false);
  const brushModeRef = React.useRef<"select" | "zoom" | null>(null);
  // Measure tabs height to avoid overlap when toolbar fully expanded
  const tabsListWrapperRef = React.useRef<HTMLDivElement | null>(null);
  const [tabsTopOffset, setTabsTopOffset] = React.useState<number>(56);
  React.useEffect(() => {
    const measure = () => {
      const el = tabsListWrapperRef.current;
      if (!el) return;
      setTabsTopOffset(el.getBoundingClientRect().height);
    };
    measure();
    const ro = new ResizeObserver(() => measure());
    const el = tabsListWrapperRef.current;
    if (el) ro.observe(el);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);

  // Close context menu on window click
  React.useEffect(() => {
    const onWinClick = () => setContextMenuOpen(false);
    window.addEventListener("click", onWinClick);
    return () => window.removeEventListener("click", onWinClick);
  }, []);

  // Cancel global brush on Escape
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        const inst = chartRef.current?.getEchartsInstance?.();
        if (inst) {
          try {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            inst.dispatchAction({
              type: "takeGlobalCursor",
              key: "brush",
              brushOption: { brushType: false } as unknown as any,
            });
          } catch {}
        }
        brushModeRef.current = null;
        zoomOnNextBrushRef.current = false;
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Known variable identifiers for editor completions (data + derived)
  const knownVariableIdsForEditor = React.useMemo(() => {
    const base = (availableAttrs ?? []).map((a) => a.name);
    const derived = Object.values(derivedDefs ?? {}).map((d: any) => d.name);
    return Array.from(new Set([...(base || []), ...(derived || [])]));
  }, [availableAttrs, derivedDefs]);

  // Build legend items for toolbar
  const legendItems = React.useMemo(() => {
    const items: {
      color: string;
      label: string;
      seriesName: string;
      hidden: boolean;
      seriesKey: string;
      fineHidden: boolean;
    }[] = [];
    const safeFermVisibility = (activeTab.fermVisibility ?? {}) as Record<
      string,
      boolean
    >;
    const safeFineVisibility = (activeTab.fineVisibility ?? {}) as Record<
      string,
      boolean
    >;
    const safeDerivedSeries = (py.results ?? {}) as Record<
      string,
      SeriesPoints
    >;
    const online = data?.["fermentation-online"] as
      | { [variable: string]: { [series: string]: [number, number][] } }
      | undefined;
    // data variables
    for (const sel of activeTab.selectedVariants) {
      const bundle = bundlesByKey[sel.key];
      if (!bundle) continue;
      const chartId = bundle.variants[sel.variant];
      if (!chartId) continue;
      const varLabel = `${bundle.displayName}${
        sel.variant === "value"
          ? ""
          : sel.variant === "setpoint"
            ? " (Setpoint)"
            : " (Output)"
      }`;
      const chartData = online?.[chartId] || {};
      Object.keys(chartData).forEach((seriesName) => {
        const hidden = safeFermVisibility[seriesName] === false;
        const color =
          colorMode === "byVariable"
            ? variableColorMap[chartId] || "#888"
            : seriesColorMap[seriesName] || "#888";
        const seriesKey = `${seriesName}::${chartId}`;
        const fineHidden = safeFineVisibility[seriesKey] === false;
        items.push({
          color,
          label: `${seriesName} - ${varLabel}`,
          seriesName,
          hidden: hidden || fineHidden,
          seriesKey,
          fineHidden,
        });
      });
    }
    // derived variables
    Object.entries(safeDerivedSeries).forEach(([dvName, seriesMap]) => {
      Object.keys(seriesMap).forEach((seriesName) => {
        const hidden = safeFermVisibility[seriesName] === false;
        const color =
          colorMode === "byVariable"
            ? variableColorMap[dvName] || "#555"
            : seriesColorMap[seriesName] || "#555";
        const seriesKey = `${seriesName}::${dvName}`;
        const fineHidden = safeFineVisibility[seriesKey] === false;
        items.push({
          color,
          label: `${seriesName} - ${dvName}`,
          seriesName,
          hidden: hidden || fineHidden,
          seriesKey,
          fineHidden,
        });
      });
    });
    return items;
  }, [
    activeTab.id,
    activeTab.selectedVariants,
    py.results,
    activeTab.fermVisibility,
    bundlesByKey,
    data,
    colorMode,
    variableColorMap,
    seriesColorMap,
  ]);

  const onChartContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    setContextMenuPos({ x: e.clientX, y: e.clientY });
    setContextMenuOpen(true);
  };

  // Compute rows for active selection
  type SelectionRow = {
    seriesId: string;
    seriesName: string;
    variableId: string;
    t: number;
    y: number;
  };
  const computeSelectionRows = React.useCallback(
    (
      xMin: number,
      xMax: number,
    ): { rows: SelectionRow[]; seriesIds: string[] } => {
      const rows: SelectionRow[] = [];
      const seriesIds: string[] = [];
      const safeFermVisibility = (activeTab.fermVisibility ?? {}) as Record<
        string,
        boolean
      >;
      const safeFineVisibility = (activeTab.fineVisibility ?? {}) as Record<
        string,
        boolean
      >;

      const online = (data?.["fermentation-online"] ?? undefined) as unknown as
        | { [variableId: string]: { [seriesName: string]: [number, number][] } }
        | undefined;
      for (const sel of activeTab.selectedVariants) {
        const varId = bundlesByKey[sel.key]?.variants[sel.variant];
        if (!varId) continue;
        const chartData = online?.[varId];
        if (!chartData) continue;
        Object.entries(chartData).forEach(([seriesName, pts]) => {
          const seriesKey = `${seriesName}::${varId}`;
          if (safeFineVisibility[seriesKey] === false) return;
          if (safeFermVisibility[seriesName] === false) return;
          const filtered = (pts || []).filter(
            (p) => p && p.length >= 2 && p[0] >= xMin && p[0] <= xMax,
          );
          if (filtered.length > 0) {
            if (!seriesIds.includes(seriesKey)) seriesIds.push(seriesKey);
            for (const [t, y] of filtered)
              rows.push({
                seriesId: seriesKey,
                seriesName,
                variableId: varId,
                t,
                y,
              });
          }
        });
      }
      const derivedSeries = (py.results ?? {}) as Record<string, SeriesPoints>;
      Object.entries(derivedSeries).forEach(([dvName, seriesMap]) => {
        Object.entries(seriesMap).forEach(([seriesName, pts]) => {
          const seriesKey = `${seriesName}::${dvName}`;
          if (safeFineVisibility[seriesKey] === false) return;
          if (safeFermVisibility[seriesName] === false) return;
          const filtered = (pts || []).filter(
            (p) => p && p.length >= 2 && p[0] >= xMin && p[0] <= xMax,
          );
          if (filtered.length > 0) {
            if (!seriesIds.includes(seriesKey)) seriesIds.push(seriesKey);
            for (const [t, y] of filtered)
              rows.push({
                seriesId: seriesKey,
                seriesName,
                variableId: dvName,
                t,
                y,
              });
          }
        });
      });
      rows.sort(
        (a, b) => a.t - b.t || a.seriesName.localeCompare(b.seriesName),
      );
      return { rows, seriesIds };
    },
    [
      activeTab.fermVisibility,
      activeTab.fineVisibility,
      activeTab.selectedVariants,
      bundlesByKey,
      data,
      py.results,
    ],
  );

  const beginBrush = (type: "lineX" | "rect", mode: "select" | "zoom") => {
    const inst = chartRef.current?.getEchartsInstance?.();
    if (!inst) return;
    try {
      console.log("begin brush", type, mode);
      inst.dispatchAction({ type: "brush", areas: [] });
      console.log("dispatch action", { type: "brush", areas: [] });
      inst.dispatchAction({
        type: "takeGlobalCursor",
        key: "brush",
        brushOption: { brushType: type, brushMode: "single" },
      });
      console.log("dispatch action", {
        type: "takeGlobalCursor",
        key: "brush",
        brushOption: { brushType: type, brushMode: "single" },
      });
      brushModeRef.current = mode;
      zoomOnNextBrushRef.current = mode === "zoom";
    } catch {}
  };

  const clearBrush = () => {
    const inst = chartRef.current?.getEchartsInstance?.();
    if (!inst) return;
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      inst.dispatchAction({
        type: "takeGlobalCursor",
        key: "brush",
        brushOption: { brushType: false } as unknown as any,
      });
      inst.dispatchAction({ type: "brush", areas: [] });
    } catch {}
    brushModeRef.current = null;
    zoomOnNextBrushRef.current = false;
  };

  const handleBrush = (evt: unknown) => {
    console.log("brush event", evt);
    // Try to read areas from multiple shapes (brush, brushend, brushselected batch)
    const anyEvt = evt as any;
    const areasTop = (anyEvt && anyEvt.areas) as
      | Array<{
          coordRange?: [number, number][] | number[];
          brushType?: string;
        }>
      | undefined;
    const areasBatch = (anyEvt &&
      anyEvt.batch &&
      anyEvt.batch[0] &&
      anyEvt.batch[0].areas) as
      | Array<{
          coordRange?: [number, number][] | number[];
          brushType?: string;
        }>
      | undefined;
    const areas = areasTop && areasTop.length ? areasTop : areasBatch;
    const area = areas && areas[0];
    // If areas empty, try to infer from current graphic elements (fallback)
    if (!area) {
      const inst = chartRef.current?.getEchartsInstance?.();
      try {
        const model: any = (inst as any)?.getModel?.();
        const brushModel = model?.getComponent?.("brush");
        const areasFromModel =
          brushModel?.option?.brush?.areas || brushModel?.option?.areas;
        if (areasFromModel && areasFromModel.length && areasTop) {
          areasTop.push(
            areasFromModel[0] as {
              coordRange?: [number, number][] | number[];
              brushType?: string;
            },
          );
        }
      } catch {}
    }
    if (!areasTop || areasTop.length === 0) return;
    const first = areasTop[0];
    let xMin: number | null = null;
    let xMax: number | null = null;
    let yMin: number | null = null;
    let yMax: number | null = null;
    const range = (first?.coordRange ?? area?.coordRange) as unknown;
    if (Array.isArray(range)) {
      // lineX -> [min, max]; rect -> [[xMin, xMax], [yMin, yMax]]
      if (
        range.length === 2 &&
        typeof range[0] === "number" &&
        typeof range[1] === "number"
      ) {
        xMin = range[0] as number;
        xMax = range[1] as number;
      } else if (Array.isArray((range as unknown[])[0])) {
        const rx = (range as unknown[])[0] as [number, number];
        xMin = rx[0];
        xMax = rx[1];
        const ry = (range as unknown[])[1] as [number, number];
        yMin = Math.min(ry[0], ry[1]);
        yMax = Math.max(ry[0], ry[1]);
      }
    }
    if (xMin == null || xMax == null) return;
    if (xMin > xMax) {
      const tmp = xMin;
      xMin = xMax;
      xMax = tmp;
    }

    if (zoomOnNextBrushRef.current) {
      const inst = chartRef.current?.getEchartsInstance?.();
      if (inst) {
        try {
          inst.dispatchAction({
            type: "dataZoom",
            startValue: xMin,
            endValue: xMax,
            xAxisIndex: 0,
          });
          if (yMin != null && yMax != null) {
            for (let yi = 0; yi < 6; yi++) {
              try {
                inst.dispatchAction({
                  type: "dataZoom",
                  startValue: yMin,
                  endValue: yMax,
                  yAxisIndex: yi,
                });
              } catch {}
            }
          }
        } catch {}
      }
      clearBrush();
      return;
    }

    const { rows, seriesIds } = computeSelectionRows(xMin, xMax);
    runtime.update(activeTab.id, (rt) => ({
      ...rt,
      selection: {
        active: true,
        xMin,
        xMax,
        seriesIds,
        rows,
        lastUpdated: Date.now(),
      },
      selectionPanel: { ...rt.selectionPanel, open: true },
    }));
    clearBrush();
  };

  // Empty state: replace whole page when no groups are selected
  if ((selectedGroups?.length ?? 0) === 0) {
    return (
      <div className="flex flex-col min-h-0 h-full w-full">
        <div className="relative h-full w-full min-h-0 min-w-0">
          <div className="absolute inset-0 flex items-center justify-center p-6">
            <div className="max-w-lg w-full text-center space-y-4">
              <div className="space-y-2">
                <h2 className="text-xl font-semibold">
                  Start exploring fermentations
                </h2>
                <p className="text-muted-foreground">
                  Select one or more fermentation groups to populate the
                  explorer.
                </p>
              </div>
              <div className="max-w-md mx-auto">
                <Combobox
                  options={availableGroups.map((g) => ({
                    value: String(g.group_id),
                    label: g.group_name ?? String(g.group_id),
                  }))}
                  placeholder="Select fermentation groups..."
                  searchPlaceholder="Search groups..."
                  emptyState="No groups found."
                  onValueChange={(value) => {
                    const groupId = Number(value) as FermentationGroupId;
                    setSelectedGroups((prev) =>
                      prev?.includes(groupId)
                        ? prev
                        : [...(prev ?? []), groupId],
                    );
                  }}
                  value={""}
                  triggerClassName="w-full"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-0 h-full w-full">
      <div className="relative h-full w-full min-h-0 min-w-0">
        {/* Floating button to show sidebar */}
        {!showRight && (
          <button
            className="fixed right-4 top-4 z-20 h-10 w-10 border rounded-md bg-background/70 hover:bg-background transition-colors backdrop-blur inline-flex items-center justify-center shadow-lg"
            title="Show derived"
            onClick={() => setShowRight(true)}
          >
            <PanelRight className="h-4 w-4" />
          </button>
        )}

        {/* Right sidebar with slide-in animation */}
        <div
          className={cn(
            "fixed right-2 bottom-2 z-20 w-[360px] border rounded-md shadow-lg backdrop-blur flex flex-col min-h-0 transition-all duration-300",
            "bg-background opacity-80 hover:opacity-100 focus-within:opacity-100",
            showRight
              ? "translate-x-0"
              : "translate-x-full opacity-0 pointer-events-none",
          )}
          style={{ top: tabsTopOffset + 8 }}
        >
          <div className="p-2 border-b flex items-center justify-between">
            <div className={cn("font-medium", DENSE.textSm)}>
              Labels & Derived
            </div>
            <Button
              size="sm"
              variant="outline"
              className={DENSE.chip}
              title="Hide derived"
              onClick={() => setShowRight(false)}
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
          <div className="flex-1 min-h-0">
            {/* Labels list */}
            <div className="border-b p-2">
              <div
                className={cn("mb-1", DENSE.textSm, "text-muted-foreground")}
              >
                Labels
              </div>
              {(
                ((nbState?.labels ?? []) as NonNullable<
                  NotebookState["labels"]
                >) ?? []
              ).length === 0 ? (
                <div className={cn(DENSE.textSm, "text-muted-foreground")}>
                  No labels.
                </div>
              ) : (
                <div className="max-h-36 overflow-auto divide-y">
                  {(
                    (nbState?.labels ?? []) as NonNullable<
                      NotebookState["labels"]
                    >
                  ).map((lab) => (
                    <div
                      key={lab!.id}
                      className="px-2 py-1 text-sm flex items-center gap-2"
                    >
                      <span
                        className="inline-block h-2 w-2 rounded"
                        style={{
                          backgroundColor: lab.color ?? "var(--primary)",
                        }}
                      />
                      <div className="truncate" title={lab.name}>
                        {lab.name}
                      </div>
                      <div className="ml-auto text-muted-foreground">
                        [{Number(lab.region?.xMin).toFixed(1)}–
                        {Number(lab.region?.xMax).toFixed(1)}]
                      </div>
                      <button
                        className="text-muted-foreground hover:text-foreground"
                        title="Remove label"
                        onClick={() =>
                          updateState((prev) => ({
                            ...prev,
                            labels: (prev.labels ?? []).filter(
                              (x) => x!.id !== lab.id,
                            ),
                          }))
                        }
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <DerivedPanel
              variables={Object.fromEntries(
                (
                  Object.values(derivedDefs) as {
                    name: string;
                    expression: string;
                  }[]
                ).map((dv) => [
                  dv.name,
                  {
                    name: dv.name,
                    expression: dv.expression,
                    error: (py.errors ?? {})[dv.name] ?? null,
                  },
                ]),
              )}
              pending={py.pending}
              selected={runtime.get(activeTab.id).selectedDerived}
              onSelect={(name) =>
                runtime.update(activeTab.id, (rt) => ({
                  ...rt,
                  selectedDerived: name,
                }))
              }
              onAdd={(name) => {
                if (!name) return;
                updateState((prev) => ({
                  ...prev,
                  derived: {
                    ...prev.derived,
                    variables: {
                      ...(prev.derived?.variables ?? {}),
                      [name]: { name, expression: "v('temp_pv')" },
                    },
                  },
                }));
                runtime.update(activeTab.id, (rt) => ({
                  ...rt,
                  selectedDerived: name,
                }));
                // Auto-evaluate new variable with default expression
                py.evalOne(name, "v('temp_pv')");
              }}
              onRemove={(name) => {
                updateState((prev) => {
                  const vars = { ...(prev.derived?.variables ?? {}) } as Record<
                    string,
                    any
                  >;
                  delete vars[name];
                  return {
                    ...prev,
                    derived: { ...prev.derived, variables: vars },
                  };
                });
                // Clear computed state and selection when removing
                py.clear(name);
                runtime.update(activeTab.id, (rt) => ({
                  ...rt,
                  selectedDerived:
                    rt.selectedDerived === name ? null : rt.selectedDerived,
                }));
              }}
              onChangeExpression={(name, expr) => {
                updateState((prev) => {
                  const v = prev.derived?.variables?.[name];
                  if (!v) return prev;
                  return {
                    ...prev,
                    derived: {
                      ...prev.derived,
                      variables: {
                        ...prev.derived!.variables,
                        [name]: { ...v, expression: expr },
                      },
                    },
                  };
                });
                // Auto-evaluate on change (per variable)
                const dv = (
                  derivedDefs as Record<
                    string,
                    { name: string; expression: string }
                  >
                )[name];
                const nextExpr = dv ? dv.expression : expr;
                py.evalOne(name, nextExpr);
              }}
              onEval={(name) => {
                const dv = (
                  derivedDefs as Record<
                    string,
                    { name: string; expression: string }
                  >
                )[name];
                if (dv) py.evalOne(name, dv.expression);
              }}
              onEvalAll={() =>
                py.evalMany(
                  Object.values(derivedDefs ?? {}) as unknown as {
                    name: string;
                    expression: string;
                  }[],
                )
              }
              knownVariableIdsForEditor={knownVariableIdsForEditor}
              inspectMap={py.inspect}
              seriesByVar={py.results}
            />
          </div>
        </div>

        {/* Center full-bleed chart and tabs */}
        <div className="absolute inset-0">
          <div className="h-full w-full min-w-0 min-h-0 relative flex flex-col">
            <SlimTabs
              value={activeTabId}
              onValueChange={(v) =>
                updateState((prev) => ({ ...prev, activeTabId: v }))
              }
              className="flex-1 min-h-0 flex flex-col"
            >
              <div ref={tabsListWrapperRef}>
                <div className="flex items-center gap-2">
                  {/* Minimal notebook switcher */}
                  <NotebookSwitcher
                    currentId={selectedNotebookId}
                    onSelect={(id) =>
                      navigate({
                        replace: true,
                        search: (prev) => ({
                          ...prev,
                          notebook: id ?? undefined,
                        }),
                      })
                    }
                  />
                  <SlimTabsList>
                    {tabs.map((t) => (
                      <SlimTabsTrigger key={t.id} value={t.id}>
                        {renamingTabId === t.id ? (
                          <Input
                            autoFocus
                            value={tabTitleDraft}
                            onChange={(e) => setTabTitleDraft(e.target.value)}
                            onBlur={() => commitRename()}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") commitRename();
                              if (e.key === "Escape") cancelRename();
                            }}
                            className="max-w-[100px] bg-background border rounded px-1 text-sm h-5"
                            inputClassName="rounded-none border-none h-5 text-sm py-0.5"
                          />
                        ) : (
                          <span
                            className="truncate max-w-[160px]"
                            onDoubleClick={() => startRename(t.id, t.title)}
                            title="Double-click to rename"
                          >
                            {t.title}
                          </span>
                        )}
                        <div
                          className="ml-2 inline-flex items-center justify-center hover:bg-muted/40 rounded"
                          onClick={(e) => {
                            e.stopPropagation();
                            closeTab(t.id);
                          }}
                          title="Close tab"
                        >
                          <X className="h-3 w-3" />
                        </div>
                      </SlimTabsTrigger>
                    ))}
                    <button
                      className="ml-2 inline-flex items-center justify-center my-auto px-2 py-1 text-sm hover:bg-muted"
                      onClick={(e) => {
                        e.stopPropagation();
                        addTab();
                      }}
                      title="Add tab"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </SlimTabsList>
                </div>
              </div>

              {tabs.map((t) => (
                <SlimTabsContent
                  key={t.id}
                  value={t.id}
                  className="flex-1 min-h-0 mt-0"
                >
                  <div className="h-full w-full min-w-0 min-h-0 relative pb-4">
                    {/* Floating integrated toolbar */}
                    <FloatingToolbar
                      toolbarState={toolbarState}
                      setToolbarState={setToolbarState}
                      toolbarPos={toolbarPos}
                      toolbarRef={toolbarElRef}
                      onToolbarPointerDown={onToolbarPointerDown}
                      onToolbarPointerMove={onToolbarPointerMove}
                      onToolbarPointerUp={onToolbarPointerUp}
                      tabsTopOffset={tabsTopOffset}
                      colorMode={colorMode}
                      onSelectColorMode={(mode) => setColorMode(mode)}
                      onReset={() => {
                        const inst = chartRef.current?.getEchartsInstance?.();
                        if (!inst) return;
                        try {
                          inst.dispatchAction({
                            type: "dataZoom",
                            start: 0,
                            end: 100,
                          });
                          inst.dispatchAction({
                            type: "dataZoom",
                            yAxisIndex: 0,
                            start: 0,
                            end: 100,
                          });
                          inst.dispatchAction({
                            type: "dataZoom",
                            yAxisIndex: 1,
                            start: 0,
                            end: 100,
                          });
                        } catch {}
                      }}
                      onSave={() => {
                        const inst = chartRef.current?.getEchartsInstance?.();
                        if (!inst) return;
                        const url = inst.getDataURL({
                          pixelRatio: 2,
                          backgroundColor: "#fff",
                        });
                        const a = document.createElement("a");
                        a.href = url;
                        a.download = "explorer-chart.png";
                        a.click();
                      }}
                      legendItems={legendItems}
                      fermentations={fermentations}
                      seriesColorMap={seriesColorMap}
                      fermVisibility={t.fermVisibility}
                      onToggleFermentation={(name, on) =>
                        updateState((prev) => ({
                          ...prev,
                          tabs: prev.tabs.map((tab: any) =>
                            tab.id === t.id
                              ? {
                                  ...tab,
                                  fermVisibility: {
                                    ...tab.fermVisibility,
                                    [name]: on,
                                  },
                                }
                              : tab,
                          ),
                        }))
                      }
                      onSetAll={(on) =>
                        updateState((prev) => ({
                          ...prev,
                          tabs: prev.tabs.map((tab: any) =>
                            tab.id === t.id
                              ? {
                                  ...tab,
                                  fermVisibility: Object.fromEntries(
                                    fermentations.map((f) => [f.name, on]),
                                  ),
                                }
                              : tab,
                          ),
                        }))
                      }
                      onToggleSeries={(seriesKey) =>
                        updateState((prev) => ({
                          ...prev,
                          tabs: prev.tabs.map((tab: any) =>
                            tab.id === t.id
                              ? {
                                  ...tab,
                                  fineVisibility: {
                                    ...tab.fineVisibility,
                                    [seriesKey]:
                                      tab.fineVisibility[seriesKey] === false
                                        ? true
                                        : false,
                                  },
                                }
                              : tab,
                          ),
                        }))
                      }
                      availableGroups={availableGroups}
                      selectedGroups={selectedGroups}
                      setSelectedGroups={setSelectedGroups}
                      bundles={bundles}
                      Sparkline={Sparkline}
                      data={data}
                      seriesColorMapProp={seriesColorMap}
                      variableColorMap={variableColorMap}
                      colorModeProp={colorMode}
                      toggleVariant={toggleVariant}
                      search={search}
                      setSearch={setSearch}
                      selectedVariants={t.selectedVariants}
                      groupMode={groupMode}
                      setGroupMode={setGroupMode}
                      singleSelection={singleSelection}
                      setSingleSelection={setSingleSelection}
                      fermsByGroup={fermsByGroup}
                      groupNameById={groupNameById}
                      fermDataSummaryByGroup={fermDataSummaryByGroup}
                      onFocusFermentation={(groupId, fermentationId) => {
                        const list = (fermsByGroup[groupId] ?? []) as {
                          id: number;
                          name: string;
                        }[];
                        const vis = Object.fromEntries(
                          list.map((f) => [f.name, f.id === fermentationId]),
                        ) as Record<string, boolean>;
                        updateState((prev) => ({
                          ...prev,
                          selectedGroups: [groupId],
                          tabs: prev.tabs.map((tab: any) =>
                            tab.id === t.id
                              ? { ...tab, fermVisibility: vis }
                              : tab,
                          ),
                        }));
                      }}
                    />

                    {/* Chart uses memo based on activeTab; render only for visible content */}
                    {activeTabId === t.id && (
                      <div
                        className="h-full w-full"
                        onContextMenu={onChartContextMenu}
                      >
                        <ReactECharts
                          ref={chartRef}
                          option={chartOption}
                          notMerge={true}
                          style={{ height: "100%", width: "100%" }}
                          opts={{ renderer: "canvas" }}
                          lazyUpdate={true}
                          onEvents={{
                            brush: handleBrush,
                            brushSelected: handleBrush,
                          }}
                        />
                      </div>
                    )}
                    {/* Floating selection panel */}
                    <SelectionPanel
                      open={
                        runtime.get(t.id).selectionPanel.open &&
                        runtime.get(t.id).selection.active
                      }
                      collapsed={runtime.get(t.id).selectionPanel.collapsed}
                      pos={runtime.get(t.id).selectionPanel.pos}
                      onToggle={() =>
                        runtime.update(t.id, (rt) => ({
                          ...rt,
                          selectionPanel: {
                            ...rt.selectionPanel,
                            collapsed: !rt.selectionPanel.collapsed,
                          },
                        }))
                      }
                      onClose={() =>
                        runtime.update(t.id, (rt) => ({
                          ...rt,
                          selectionPanel: { ...rt.selectionPanel, open: false },
                        }))
                      }
                      onDrag={(dx, dy) =>
                        runtime.update(t.id, (rt) => ({
                          ...rt,
                          selectionPanel: {
                            ...rt.selectionPanel,
                            pos: {
                              x: Math.max(8, rt.selectionPanel.pos.x + dx),
                              y: Math.max(
                                tabsTopOffset + 8,
                                rt.selectionPanel.pos.y + dy,
                              ),
                            },
                          },
                        }))
                      }
                      selection={runtime.get(t.id).selection}
                      onClear={() =>
                        runtime.update(t.id, (rt) => ({
                          ...rt,
                          selection: {
                            ...rt.selection,
                            active: false,
                            rows: [],
                            seriesIds: [],
                            xMin: null,
                            xMax: null,
                          },
                        }))
                      }
                      onCreateLabel={(labelName) => {
                        const sel = runtime.get(t.id).selection;
                        if (!sel.active || sel.xMin == null || sel.xMax == null)
                          return;
                        updateState((prev) => ({
                          ...prev,
                          labels: [
                            ...((prev.labels ?? []) as any[]),
                            {
                              id: nanoid(),
                              name:
                                labelName ||
                                `Selection ${new Date().toLocaleString()}`,
                              color: null,
                              region: { xMin: sel.xMin!, xMax: sel.xMax! },
                              seriesIds: sel.seriesIds.slice(),
                              createdAt: new Date().toISOString(),
                            },
                          ],
                        }));
                      }}
                    />
                    {/* Custom context menu */}
                    {contextMenuOpen && (
                      <div
                        className="fixed z-50 border rounded-md bg-background shadow-lg"
                        style={{
                          left: contextMenuPos.x,
                          top: contextMenuPos.y,
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="px-2 py-1 text-sm border-b">
                          Plot actions
                        </div>
                        <button
                          className="block w-full text-left px-3 py-1 text-sm hover:bg-muted"
                          onClick={() => {
                            beginBrush("lineX", "select");
                            setContextMenuOpen(false);
                          }}
                        >
                          Select region (X-axis)
                        </button>
                        <button
                          className="block w-full text-left px-3 py-1 text-sm hover:bg-muted"
                          onClick={() => {
                            // Use 'rect' brush for rectangle zoom
                            beginBrush("rect", "zoom");
                            setContextMenuOpen(false);
                          }}
                        >
                          Rectangle zoom
                        </button>
                        {runtime.get(t.id).selection.active && (
                          <>
                            <button
                              className="block w-full text-left px-3 py-1 text-sm hover:bg-muted"
                              onClick={() => {
                                const sel = runtime.get(t.id).selection;
                                if (sel.xMin != null && sel.xMax != null) {
                                  const inst =
                                    chartRef.current?.getEchartsInstance?.();
                                  if (inst) {
                                    try {
                                      inst.dispatchAction({
                                        type: "dataZoom",
                                        startValue: sel.xMin,
                                        endValue: sel.xMax,
                                        xAxisIndex: 0,
                                      });
                                    } catch {}
                                  }
                                }
                                setContextMenuOpen(false);
                              }}
                            >
                              Zoom to selection
                            </button>
                            <button
                              className="block w-full text-left px-3 py-1 text-sm hover:bg-muted"
                              onClick={() => {
                                runtime.update(t.id, (rt) => ({
                                  ...rt,
                                  selection: {
                                    ...rt.selection,
                                    active: false,
                                    rows: [],
                                    seriesIds: [],
                                    xMin: null,
                                    xMax: null,
                                  },
                                }));
                                setContextMenuOpen(false);
                              }}
                            >
                              Clear selection
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </SlimTabsContent>
              ))}
            </SlimTabs>
          </div>
        </div>
      </div>
    </div>
  );
}

function SelectionPanel({
  open,
  collapsed,
  pos,
  onToggle,
  onClose,
  onDrag,
  selection,
  onClear,
  onCreateLabel,
}: {
  open: boolean;
  collapsed: boolean;
  pos: { x: number; y: number };
  onToggle: () => void;
  onClose: () => void;
  onDrag: (dx: number, dy: number) => void;
  selection: {
    active: boolean;
    xMin: number | null;
    xMax: number | null;
    seriesIds: string[];
    rows: Array<{
      seriesId: string;
      seriesName: string;
      variableId: string;
      t: number;
      y: number;
    }>;
  };
  onClear: () => void;
  onCreateLabel: (name: string) => void;
}) {
  const [dragging, setDragging] = React.useState(false);
  const lastRef = React.useRef<{ x: number; y: number } | null>(null);
  if (!open) return null;

  const onPointerDown = (e: React.PointerEvent) => {
    setDragging(true);
    lastRef.current = { x: e.clientX, y: e.clientY };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragging || !lastRef.current) return;
    const dx = e.clientX - lastRef.current.x;
    const dy = e.clientY - lastRef.current.y;
    onDrag(dx, dy);
    lastRef.current = { x: e.clientX, y: e.clientY };
  };
  const onPointerUp = (e: React.PointerEvent) => {
    setDragging(false);
    try {
      (e.target as HTMLElement).releasePointerCapture(e.pointerId);
    } catch {}
  };

  // Group rows by series then compute quick stats per column
  const grouped = React.useMemo(() => {
    const m = new Map<
      string,
      { seriesName: string; variableId: string; points: [number, number][] }
    >();
    for (const r of selection.rows) {
      if (!m.has(r.seriesId))
        m.set(r.seriesId, {
          seriesName: r.seriesName,
          variableId: r.variableId,
          points: [],
        });
      m.get(r.seriesId)!.points.push([r.t, r.y]);
    }
    return Array.from(m.entries()).map(([seriesId, v]) => ({ seriesId, ...v }));
  }, [selection.rows]);

  const csv = React.useMemo(() => {
    const header = ["seriesId", "seriesName", "variableId", "t", "y"].join(",");
    const lines = selection.rows.map(
      (r) => `${r.seriesId},${r.seriesName},${r.variableId},${r.t},${r.y}`,
    );
    return [header, ...lines].join("\n");
  }, [selection.rows]);

  const downloadCsv = () => {
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "selection.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const [calc, setCalc] = React.useState<
    "mean" | "median" | "sum" | "integral" | null
  >(null);
  const computeFor = (type: "mean" | "median" | "sum" | "integral") =>
    setCalc(type);

  return (
    <div
      className="fixed z-30 border rounded-md shadow-lg bg-background/90 backdrop-blur min-w-[360px]"
      style={{ left: pos.x, top: pos.y }}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
    >
      <div
        className="px-2 py-1 border-b flex items-center gap-2 cursor-grab select-none"
        onPointerDown={onPointerDown}
      >
        <div className="font-medium text-sm">Selection</div>
        <div className="text-xs text-muted-foreground">
          {selection.xMin?.toFixed(2)} – {selection.xMax?.toFixed(2)} h
        </div>
        <div className="ml-auto inline-flex items-center gap-1">
          <Button
            size="sm"
            variant="outline"
            className={DENSE.chip}
            onClick={onToggle}
          >
            {collapsed ? "Expand" : "Collapse"}
          </Button>
          <Button
            size="sm"
            variant="outline"
            className={DENSE.chip}
            onClick={downloadCsv}
          >
            Export CSV
          </Button>
          <Button
            size="sm"
            variant="outline"
            className={DENSE.chip}
            onClick={onClear}
          >
            Clear
          </Button>
          <Button
            size="sm"
            className={DENSE.chip}
            onClick={() => {
              const name = window.prompt("Label name?", "Selection");
              if (name) onCreateLabel(name);
            }}
          >
            Label
          </Button>
          <Button
            size="sm"
            variant="outline"
            className={DENSE.chip}
            onClick={onClose}
          >
            Close
          </Button>
        </div>
      </div>
      {!collapsed && (
        <div className="max-h-[280px] overflow-auto">
          <table className="w-full text-xs">
            <thead>
              <tr>
                <th className="text-left px-2 py-1 border-b">Series</th>
                <th className="text-left px-2 py-1 border-b">Variable</th>
                <th className="text-left px-2 py-1 border-b">t</th>
                <th className="text-left px-2 py-1 border-b">y</th>
              </tr>
            </thead>
            <tbody>
              {selection.rows.map((r, idx) => (
                <tr key={idx} className="border-b last:border-b-0">
                  <td className="px-2 py-1">{r.seriesName}</td>
                  <td className="px-2 py-1">{r.variableId}</td>
                  <td className="px-2 py-1">{r.t}</td>
                  <td className="px-2 py-1">{r.y}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {/* Notion-style calculate row */}
          <div className="border-t p-2">
            <div className="flex items-center gap-2 flex-wrap">
              <div className={cn(DENSE.textSm, "text-muted-foreground")}>
                Calculate:
              </div>
              <Button
                size="sm"
                variant="outline"
                className={DENSE.chip}
                onClick={() => computeFor("mean")}
              >
                Mean
              </Button>
              <Button
                size="sm"
                variant="outline"
                className={DENSE.chip}
                onClick={() => computeFor("median")}
              >
                Median
              </Button>
              <Button
                size="sm"
                variant="outline"
                className={DENSE.chip}
                onClick={() => computeFor("sum")}
              >
                Sum
              </Button>
              <Button
                size="sm"
                variant="outline"
                className={DENSE.chip}
                onClick={() => computeFor("integral")}
              >
                Integral
              </Button>
            </div>
            {calc && (
              <div className="mt-2 border rounded">
                <table className="w-full text-xs">
                  <thead>
                    <tr>
                      <th className="text-left px-2 py-1 border-b">Series</th>
                      <th className="text-left px-2 py-1 border-b">Variable</th>
                      <th className="text-left px-2 py-1 border-b capitalize">
                        {calc}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {grouped.map((g) => {
                      const ys = g.points.map((p) => p[1]);
                      let val: number | null = null;
                      if (calc === "mean") val = mean(ys);
                      else if (calc === "median") val = median(ys);
                      else if (calc === "sum") val = sum(ys);
                      else if (calc === "integral")
                        val = trapezoidalIntegral(g.points);
                      return (
                        <tr
                          key={g.seriesId}
                          className="border-b last:border-b-0"
                        >
                          <td className="px-2 py-1">{g.seriesName}</td>
                          <td className="px-2 py-1">{g.variableId}</td>
                          <td className="px-2 py-1">
                            {val == null ? "" : val}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function NotebookSwitcher({
  currentId,
  onSelect,
}: {
  currentId: ExplorerNotebookId | null;
  onSelect: (id: ExplorerNotebookId | null) => void;
}) {
  const { data } = trpc.explorerNotebooks.listAccessible.useQuery();
  const createMutation = trpc.explorerNotebooks.create.useMutation();

  const all: { id: ExplorerNotebookId; name: string; is_scratch: boolean }[] = [
    // Scratch is implicit; not shown in list, but selected when null
    ...((data?.mine ?? []) as {
      id: ExplorerNotebookId;
      name: string;
      is_scratch: boolean;
    }[]),
    ...((data?.shared ?? []) as {
      id: ExplorerNotebookId;
      name: string;
      is_scratch: boolean;
    }[]),
  ].filter((n) => !n.is_scratch);

  return (
    <div className="flex items-center gap-1">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            size="sm"
            variant="outline"
            className={DENSE.chip}
            title="Switch notebook"
          >
            <Book className="h-3 w-3" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          sideOffset={4}
          className="min-w-[220px]"
        >
          <DropdownMenuLabel>Notebooks</DropdownMenuLabel>
          <DropdownMenuItem onClick={() => onSelect(null)}>
            {currentId ? "Scratch" : "✓ Scratch"}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          {all.length === 0 && (
            <div className="px-2 py-1 text-xs text-muted-foreground">
              No notebooks
            </div>
          )}
          {all.map((n) => (
            <DropdownMenuItem key={n.id} onClick={() => onSelect(n.id)}>
              {currentId === n.id ? "✓ " : ""}
              {n.name}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      <Button
        size="sm"
        variant="outline"
        className={DENSE.chip}
        title="New notebook"
        onClick={async () => {
          const name = (
            await promptText({
              title: "New notebook",
              description: "Enter a name",
              placeholder: "My notebook",
            })
          )
            ?.toString()
            .trim();
          if (!name) return;
          const created = await createMutation.mutateAsync({ name });
          onSelect((created as unknown as { id: ExplorerNotebookId }).id);
        }}
      >
        <FolderPlus className="h-3 w-3" />
      </Button>
      <ShareNotebookButton currentId={currentId} />
    </div>
  );
}

function ShareNotebookButton({
  currentId,
}: {
  currentId: ExplorerNotebookId | null;
}) {
  const shareMutation = trpc.explorerNotebooks.share.useMutation();
  if (!currentId) return null;
  return (
    <Button
      size="sm"
      variant="outline"
      className={DENSE.chip}
      title="Share notebook"
      onClick={async () => {
        const value = await promptText({
          title: "Share notebook",
          description: "Enter operator ID (numeric)",
          placeholder: "123",
        });
        if (!value) return;
        const operatorIdNum = Number(value);
        if (!Number.isFinite(operatorIdNum)) return;
        await shareMutation.mutateAsync({
          id: currentId,
          operator_id: operatorIdNum,
          permission: "view",
        });
      }}
    >
      <Share2 className="h-3 w-3" />
    </Button>
  );
}

function FloatingToolbar({
  toolbarState,
  setToolbarState,
  toolbarPos,
  toolbarRef,
  onToolbarPointerDown,
  onToolbarPointerMove,
  onToolbarPointerUp,
  tabsTopOffset,
  colorMode,
  onSelectColorMode,
  onReset,
  onSave,
  legendItems,
  fermentations,
  seriesColorMap,
  fermVisibility,
  onToggleFermentation,
  onSetAll,
  onToggleSeries,
  availableGroups,
  selectedGroups,
  setSelectedGroups,
  bundles,
  Sparkline,
  data,
  seriesColorMapProp,
  variableColorMap,
  colorModeProp,
  toggleVariant,
  search,
  setSearch,
  selectedVariants,
  groupMode,
  setGroupMode,
  singleSelection,
  setSingleSelection,
  fermsByGroup,
  groupNameById,
  fermDataSummaryByGroup,
  onFocusFermentation,
}: {
  toolbarState: "buttons" | "legend" | "all";
  setToolbarState: (s: "buttons" | "legend" | "all") => void;
  toolbarPos: { x: number; y: number };
  toolbarRef: React.RefObject<HTMLDivElement | null>;
  onToolbarPointerDown: (e: React.PointerEvent) => void;
  onToolbarPointerMove: (e: React.PointerEvent) => void;
  onToolbarPointerUp: (e: React.PointerEvent) => void;
  tabsTopOffset: number;
  colorMode: "byFermentation" | "byVariable";
  onSelectColorMode: (mode: "byFermentation" | "byVariable") => void;
  onReset: () => void;
  onSave: () => void;
  legendItems: {
    color: string;
    label: string;
    seriesName: string;
    hidden: boolean;
    seriesKey: string;
    fineHidden: boolean;
  }[];
  fermentations: FermentationInfo[];
  seriesColorMap: Record<string, string>;
  fermVisibility: Record<string, boolean>;
  onToggleFermentation: (name: string, on: boolean) => void;
  onSetAll: (on: boolean) => void;
  onToggleSeries: (seriesKey: string) => void;
  availableGroups: { group_id: FermentationGroupId; group_name: string }[];
  selectedGroups: FermentationGroupId[];
  setSelectedGroups: React.Dispatch<
    React.SetStateAction<FermentationGroupId[]>
  >;
  bundles: VariableBundle[];
  Sparkline: React.ComponentType<any>;
  data: Record<string, any> | null | undefined;
  seriesColorMapProp: Record<string, string>;
  variableColorMap: Record<string, string>;
  colorModeProp: "byFermentation" | "byVariable";
  toggleVariant: (b: VariableBundle, v: Variant) => void;
  search: string;
  setSearch: (v: string) => void;
  selectedVariants: { key: string; variant: Variant }[];
  groupMode: "single" | "compare" | "filter";
  setGroupMode: (m: "single" | "compare" | "filter") => void;
  singleSelection: { groupId: number | null; fermentationId: number | null };
  setSingleSelection: (
    groupId: number | null,
    fermentationId: number | null,
  ) => void;
  fermsByGroup: Record<number, { id: number; name: string }[]>;
  groupNameById: Record<number, string>;
  fermDataSummaryByGroup: Record<
    number,
    Record<
      number,
      {
        tsRecordings: number;
        tsPoints: number;
        offlineSamples: number;
        lastSampleAt: string | null;
      }
    >
  >;
  onFocusFermentation: (
    groupId: FermentationGroupId,
    fermentationId: number,
  ) => void;
}) {
  const allVisible = React.useMemo(
    () => fermentations.every((f) => fermVisibility[f.name] !== false),
    [fermentations, fermVisibility],
  );

  // Positioning styles
  const baseClasses = cn(
    "z-20 border rounded-md shadow-lg backdrop-blur transition-opacity duration-150",
    "bg-background opacity-80 hover:opacity-100 focus-within:opacity-100",
  );
  const isDocked = toolbarState === "all";
  const style: React.CSSProperties = isDocked
    ? { position: "fixed", left: 8, top: tabsTopOffset, bottom: 8, width: 360 }
    : {
        position: "fixed",
        left: toolbarPos.x,
        top: toolbarPos.y,
        willChange: "left, top",
      };

  const [previewsOn, setPreviewsOn] = React.useState(false);
  const [groupFilter, setGroupFilter] = React.useState("");
  const [groupsOpen, setGroupsOpen] = React.useState(true);
  const autoCollapsedRef = React.useRef(false);
  React.useEffect(() => {
    if (!autoCollapsedRef.current && selectedVariants.length > 0) {
      setGroupsOpen(false);
      autoCollapsedRef.current = true;
    }
  }, [selectedVariants.length]);
  const [chipMenuOpenFor, setChipMenuOpenFor] = React.useState<number | null>(
    null,
  );
  const [fermFilter, setFermFilter] = React.useState("");
  const [addMenuOpen, setAddMenuOpen] = React.useState(false);
  const [groupAddFilter, setGroupAddFilter] = React.useState("");

  return (
    <div
      className={cn(
        baseClasses,
        isDocked ? "flex flex-col min-h-0" : undefined,
      )}
      style={style}
      ref={toolbarRef}
      onPointerMove={onToolbarPointerMove}
      onPointerUp={onToolbarPointerUp}
    >
      {/* Toolbar buttons row with drag handle and expand control */}
      <div className="p-1 border-b flex items-center gap-1">
        <div
          className={cn(
            "h-6 w-4 inline-flex items-center justify-center cursor-grab select-none touch-none",
            isDocked ? "opacity-50 cursor-not-allowed" : undefined,
          )}
          onPointerDown={onToolbarPointerDown}
          title={isDocked ? "Docked" : "Drag"}
        >
          <GripVertical className="h-3 w-3" />
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              size="sm"
              variant="outline"
              className={DENSE.chip}
              title={`Color mode: ${
                colorMode === "byVariable" ? "By variable" : "By fermentation"
              }`}
            >
              <Palette className="h-3 w-3" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="start"
            sideOffset={4}
            className="min-w-[160px]"
          >
            <DropdownMenuLabel>Color mode</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => onSelectColorMode("byVariable")}>
              {colorMode === "byVariable" ? "✓ " : ""}By variable
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => onSelectColorMode("byFermentation")}
            >
              {colorMode === "byFermentation" ? "✓ " : ""}By fermentation
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <Button
          size="sm"
          variant="outline"
          className={DENSE.chip}
          onClick={onReset}
          title="Reset zoom"
        >
          <RefreshCcw className="h-3 w-3" />
        </Button>
        <Button
          size="sm"
          variant="outline"
          className={DENSE.chip}
          onClick={onSave}
          title="Save chart as PNG"
        >
          <Download className="h-3 w-3" />
        </Button>

        <Button
          size="sm"
          variant="outline"
          className={DENSE.chip}
          onClick={() => onSetAll(!allVisible)}
          title={allVisible ? "Hide all" : "Show all"}
        >
          {allVisible ? (
            <EyeOff className="h-3 w-3" />
          ) : (
            <Eye className="h-3 w-3" />
          )}
        </Button>

        {/* Compact segmented control for states */}
        <div className="ml-auto inline-flex items-stretch border rounded overflow-hidden">
          <Button
            size="sm"
            variant={toolbarState === "buttons" ? "default" : "outline"}
            className={DENSE.chip}
            onClick={() => setToolbarState("buttons")}
            title="Controls only"
            aria-label="Controls only"
          >
            <SlidersHorizontal className="h-3 w-3" />
          </Button>
          <Button
            size="sm"
            variant={toolbarState === "legend" ? "default" : "outline"}
            className={DENSE.chip}
            onClick={() => setToolbarState("legend")}
            title="Controls + legend"
            aria-label="Controls and legend"
          >
            <List className="h-3 w-3" />
          </Button>
          <Button
            size="sm"
            variant={toolbarState === "all" ? "default" : "outline"}
            className={DENSE.chip}
            onClick={() => setToolbarState("all")}
            title="Dock and expand fully"
            aria-label="Docked full toolbar"
          >
            <PanelLeft className="h-3 w-3" />
          </Button>
        </div>
      </div>

      {/* Legend section (visible for legend or all) */}
      {(toolbarState === "legend" || toolbarState === "all") && (
        <div className="max-h-[240px] overflow-auto divide-y">
          {legendItems.length === 0 && (
            <div
              className={cn("px-2 py-1", DENSE.textSm, "text-muted-foreground")}
            >
              No visible series.
            </div>
          )}
          {legendItems.map((it, idx) => (
            <div
              key={idx}
              className="px-2 py-1 flex items-center gap-2 cursor-pointer select-none"
              onClick={() => onToggleSeries(it.seriesKey)}
              title={
                it.fineHidden || fermVisibility[it.seriesName] === false
                  ? `Show ${it.seriesName}`
                  : `Hide ${it.seriesName}`
              }
            >
              <span
                className="inline-block h-2 w-2 rounded"
                style={{
                  backgroundColor: it.hidden ? "var(--muted)" : it.color,
                  opacity: it.hidden ? 0.5 : 1,
                }}
              />
              <span
                className={cn(
                  "truncate",
                  DENSE.textSm,
                  it.hidden ? "text-muted-foreground" : undefined,
                )}
                title={it.label}
              >
                {it.label}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Full content: groups and variable list */}
      {toolbarState === "all" && (
        <div className="flex-1 min-h-0 flex flex-col">
          {/* Groups selector (merged single/compare) */}
          <div
            className={cn(
              "flex flex-col gap-2",
              DENSE.padX,
              DENSE.padY,
              "border-t border-b",
            )}
          >
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                className={DENSE.chip}
                onClick={() => setGroupsOpen((v) => !v)}
                title={groupsOpen ? "Collapse" : "Expand"}
              >
                {groupsOpen ? (
                  <ChevronDown className="h-3 w-3" />
                ) : (
                  <ChevronRight className="h-3 w-3" />
                )}
              </Button>
              <div className={cn("text-sm", "font-medium")}>Fermentations</div>
              <div className="ml-auto inline-flex items-center gap-1">
                {/* Toggle filter mode via search icon */}
                <Button
                  size="sm"
                  variant={groupMode === "filter" ? "default" : "outline"}
                  className={DENSE.chip}
                  title={
                    groupMode === "filter" ? "Exit filter" : "Filter groups"
                  }
                  onClick={() =>
                    setGroupMode(groupMode === "filter" ? "compare" : "filter")
                  }
                >
                  <Search className="h-3 w-3" />
                </Button>
                {/* Add group with searchable menu */}
                <DropdownMenu open={addMenuOpen} onOpenChange={setAddMenuOpen}>
                  <DropdownMenuTrigger asChild>
                    <Button
                      size="sm"
                      variant="outline"
                      className={DENSE.chip}
                      title="Add group"
                    >
                      <Plus className="h-3 w-3" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    sideOffset={4}
                    className="min-w-[240px] max-h-[300px] overflow-auto"
                  >
                    <div className="px-2 py-1">
                      <Input
                        value={groupAddFilter}
                        onChange={(e) => setGroupAddFilter(e.target.value)}
                        placeholder="Search groups"
                        className={cn("h-7 text-sm", "rounded-none w-full")}
                        inputClassName={cn("rounded-none h-7 text-sm")}
                      />
                    </div>
                    <DropdownMenuSeparator />
                    {availableGroups
                      .filter((g) => {
                        const needle = groupAddFilter.trim().toLowerCase();
                        if (!needle) return true;
                        const name = (
                          g.group_name ?? String(g.group_id)
                        ).toLowerCase();
                        return (
                          name.includes(needle) || String(g.group_id) === needle
                        );
                      })
                      .map((g) => (
                        <DropdownMenuItem
                          key={g.group_id}
                          onClick={() => {
                            if (!selectedGroups.includes(g.group_id)) {
                              setSelectedGroups((prev) => [
                                ...prev,
                                g.group_id,
                              ]);
                            }
                          }}
                        >
                          {g.group_name ?? String(g.group_id)}
                        </DropdownMenuItem>
                      ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            {groupMode !== "filter" && groupsOpen && (
              <div className="flex flex-wrap gap-1">
                {selectedGroups.map((groupId) => {
                  const group = availableGroups.find(
                    (g) => g.group_id === groupId,
                  );
                  return (
                    <div
                      key={groupId}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        setChipMenuOpenFor(groupId);
                      }}
                    >
                      <Badge
                        variant="secondary"
                        className="flex items-center gap-1"
                      >
                        {group?.group_name ?? groupId}
                        <DropdownMenu
                          open={chipMenuOpenFor === groupId}
                          onOpenChange={(open) =>
                            setChipMenuOpenFor(open ? groupId : null)
                          }
                        >
                          <DropdownMenuTrigger asChild>
                            <button
                              className="ml-1 hover:bg-muted-foreground/20 rounded"
                              title="Fermentations"
                            >
                              <ChevronDown className="h-3 w-3" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent
                            align="start"
                            sideOffset={4}
                            className="min-w-[220px] max-h-[300px] overflow-auto"
                          >
                            <DropdownMenuLabel>Fermentations</DropdownMenuLabel>
                            <div className="px-2 py-1">
                              <Input
                                value={fermFilter}
                                onChange={(e) => setFermFilter(e.target.value)}
                                placeholder="Search"
                                className={cn(
                                  "h-7 text-sm",
                                  "rounded-none w-full",
                                )}
                                inputClassName={cn("rounded-none h-7 text-sm")}
                              />
                            </div>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() =>
                                (fermsByGroup[groupId] || []).forEach((f) =>
                                  onToggleFermentation(f.name, true),
                                )
                              }
                            >
                              Show all in group
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() =>
                                (fermsByGroup[groupId] || []).forEach((f) =>
                                  onToggleFermentation(f.name, false),
                                )
                              }
                            >
                              Hide all in group
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            {(fermsByGroup[groupId] || [])
                              .filter((f) => {
                                const needle = fermFilter.trim().toLowerCase();
                                if (!needle) return true;
                                return (
                                  f.name.toLowerCase().includes(needle) ||
                                  String(f.id) === needle
                                );
                              })
                              .map((f) => (
                                <DropdownMenuCheckboxItem
                                  key={f.id}
                                  checked={fermVisibility[f.name] !== false}
                                  onCheckedChange={(on) =>
                                    onToggleFermentation(f.name, Boolean(on))
                                  }
                                >
                                  <div className="flex flex-col gap-0.5">
                                    <span>{f.name}</span>
                                    <span className="text-xs text-muted-foreground">
                                      {(() => {
                                        const s =
                                          fermDataSummaryByGroup[groupId]?.[
                                            f.id
                                          ];
                                        if (!s) return "";
                                        const parts = [] as string[];
                                        if (s.tsRecordings > 0) {
                                          parts.push(
                                            `TS: ${s.tsRecordings} rec, ${s.tsPoints} pts`,
                                          );
                                        }
                                        if (s.offlineSamples > 0) {
                                          const d = s.lastSampleAt
                                            ? new Date(
                                                s.lastSampleAt,
                                              ).toLocaleDateString()
                                            : null;
                                          parts.push(
                                            `Offline: ${s.offlineSamples}${d ? ` (last ${d})` : ""}`,
                                          );
                                        }
                                        return parts.length
                                          ? parts.join(" · ")
                                          : "No details";
                                      })()}
                                    </span>
                                  </div>
                                </DropdownMenuCheckboxItem>
                              ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                        {selectedGroups.length > 1 && (
                          <button
                            onClick={() =>
                              setSelectedGroups((prev) =>
                                prev.filter((id) => id !== groupId),
                              )
                            }
                            className="ml-1 hover:bg-muted-foreground/20"
                            title="Remove group"
                          >
                            ×
                          </button>
                        )}
                      </Badge>
                    </div>
                  );
                })}
                {!selectedGroups.length && (
                  <span className={cn(DENSE.textSm, "text-muted-foreground")}>
                    No groups selected
                  </span>
                )}
              </div>
            )}

            {/* Filter mode */}
            {groupMode === "filter" && (
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <Input
                    value={groupFilter}
                    onChange={(e) => setGroupFilter(e.target.value)}
                    placeholder="Filter groups by name or id"
                    className={cn("h-7 text-sm", "rounded-none w-full")}
                    inputClassName={cn("rounded-none h-7 text-sm")}
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    className={DENSE.chip}
                    onClick={() => {
                      const needle = groupFilter.trim().toLowerCase();
                      const matches = availableGroups
                        .filter((g) =>
                          needle === ""
                            ? true
                            : (g.group_name ?? String(g.group_id))
                                .toLowerCase()
                                .includes(needle) ||
                              String(g.group_id) === needle,
                        )
                        .map((g) => g.group_id);
                      setSelectedGroups(matches);
                    }}
                  >
                    Select matching
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className={DENSE.chip}
                    onClick={() => setSelectedGroups([])}
                  >
                    Clear
                  </Button>
                </div>

                <div className="max-h-[140px] overflow-auto border rounded">
                  {availableGroups
                    .filter((g) => {
                      const needle = groupFilter.trim().toLowerCase();
                      if (needle === "") return true;
                      const name = (
                        g.group_name ?? String(g.group_id)
                      ).toLowerCase();
                      return (
                        name.includes(needle) || String(g.group_id) === needle
                      );
                    })
                    .slice(0, 100)
                    .map((g) => (
                      <div
                        key={g.group_id}
                        className="px-2 py-1 text-sm flex items-center justify-between border-b last:border-b-0"
                      >
                        <span className="truncate mr-2">
                          {g.group_name ?? String(g.group_id)}
                        </span>
                        <Button
                          size="sm"
                          variant={
                            selectedGroups.includes(g.group_id)
                              ? "default"
                              : "outline"
                          }
                          className={DENSE.chip}
                          onClick={() =>
                            setSelectedGroups((prev) =>
                              prev.includes(g.group_id)
                                ? prev.filter((id) => id !== g.group_id)
                                : [...prev, g.group_id],
                            )
                          }
                        >
                          {selectedGroups.includes(g.group_id)
                            ? "Added"
                            : "Add"}
                        </Button>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>

          {/* Search */}
          <div
            className={cn(
              "flex items-center",
              DENSE.padX,
              DENSE.padY,
              DENSE.gap2,
              "border-b",
            )}
          >
            <Search className="h-3 w-3 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search variables"
              className={cn("h-7 text-sm", "rounded-none border-none w-full")}
              inputClassName={cn("rounded-none border-none h-7 text-sm")}
            />
            <Button
              size="sm"
              variant="outline"
              className={DENSE.chip}
              onClick={() => setPreviewsOn((v) => !v)}
              title="Toggle previews"
            >
              {previewsOn ? "Previews" : "Previews"}
            </Button>
          </div>

          {/* Selected shelf */}
          {!!selectedVariants.length && (
            <div className="px-2 py-1 border-b">
              <div
                className={cn("mb-1", DENSE.textSm, "text-muted-foreground")}
              >
                Selected
              </div>
              <div className="flex flex-wrap gap-1">
                {selectedVariants.map((sv) => {
                  const b = bundles.find((x) => x.key === sv.key);
                  const vLabel =
                    sv.variant === "value"
                      ? ""
                      : sv.variant === "setpoint"
                        ? " (SP)"
                        : " (Out)";
                  return (
                    <Badge
                      key={`${sv.key}:${sv.variant}`}
                      variant="secondary"
                      className="h-5 text-xs gap-1"
                    >
                      {(b?.displayName ?? sv.key) + vLabel}
                      {b && (
                        <button
                          onClick={() => toggleVariant(b, sv.variant)}
                          className="ml-1 hover:bg-muted-foreground/20"
                          title="Remove"
                        >
                          ×
                        </button>
                      )}
                    </Badge>
                  );
                })}
              </div>
            </div>
          )}

          {/* Variables categorized accordion */}
          <div className="min-h-0 overflow-auto">
            {bundles.length === 0 ? (
              <div className={cn("p-2", DENSE.textSm, "text-muted-foreground")}>
                No variables.
              </div>
            ) : (
              <Accordion type="multiple" className="min-h-0">
                {Array.from(
                  bundles.reduce((acc, b) => {
                    const k = b.category ?? "Other";
                    if (!acc.has(k)) acc.set(k, [] as VariableBundle[]);
                    acc.get(k)!.push(b);
                    return acc;
                  }, new Map<string, VariableBundle[]>()),
                )
                  .sort((a, b) => a[0].localeCompare(b[0]))
                  .map(([cat, items]) => (
                    <AccordionItem key={cat} value={cat} className="border-b">
                      <AccordionTrigger className="px-2 py-1 text-sm">
                        {cat}
                      </AccordionTrigger>
                      <AccordionContent className="pt-1">
                        <div className="flex flex-col">
                          {items
                            .slice()
                            .sort((a, b) =>
                              a.displayName.localeCompare(b.displayName),
                            )
                            .map((b) => {
                              const varId =
                                b.variants.value ||
                                b.variants.setpoint ||
                                b.variants.output ||
                                b.key;
                              const colorDot =
                                colorModeProp === "byVariable"
                                  ? variableColorMap[varId ?? ""] || undefined
                                  : undefined;
                              const isSel = (v: Variant) =>
                                selectedVariants.some(
                                  (s) => s.key === b.key && s.variant === v,
                                );
                              return (
                                <div
                                  key={b.key}
                                  className={cn("px-2 py-1", DENSE.padY)}
                                >
                                  <div className="flex items-center justify-between gap-2">
                                    <div className="flex items-center gap-2 min-w-0">
                                      <span
                                        className="inline-block h-2 w-2 rounded"
                                        style={{ backgroundColor: colorDot }}
                                      />
                                      <div
                                        className={cn("truncate", DENSE.textSm)}
                                        title={b.displayName}
                                      >
                                        {b.displayName}
                                      </div>
                                      {b.unit && (
                                        <span
                                          className={cn(
                                            "text-muted-foreground",
                                            DENSE.textSm,
                                          )}
                                        >
                                          [{b.unit}]
                                        </span>
                                      )}
                                    </div>
                                    <div className="inline-flex items-center gap-1">
                                      {b.variants.value && (
                                        <Button
                                          size="sm"
                                          variant={
                                            isSel("value")
                                              ? "default"
                                              : "outline"
                                          }
                                          className={DENSE.chip}
                                          onClick={() =>
                                            toggleVariant(b, "value")
                                          }
                                          title="Value"
                                        >
                                          Val
                                        </Button>
                                      )}
                                      {b.variants.setpoint && (
                                        <Button
                                          size="sm"
                                          variant={
                                            isSel("setpoint")
                                              ? "default"
                                              : "outline"
                                          }
                                          className={DENSE.chip}
                                          onClick={() =>
                                            toggleVariant(b, "setpoint")
                                          }
                                          title="Setpoint"
                                        >
                                          SP
                                        </Button>
                                      )}
                                      {b.variants.output && (
                                        <Button
                                          size="sm"
                                          variant={
                                            isSel("output")
                                              ? "default"
                                              : "outline"
                                          }
                                          className={DENSE.chip}
                                          onClick={() =>
                                            toggleVariant(b, "output")
                                          }
                                          title="Output"
                                        >
                                          Out
                                        </Button>
                                      )}
                                    </div>
                                  </div>
                                  {previewsOn && (
                                    <div className="mt-1">
                                      <Sparkline
                                        data={data}
                                        seriesColorMap={seriesColorMapProp}
                                        variableColorMap={variableColorMap}
                                        colorMode={colorModeProp}
                                        variableId={varId ?? ""}
                                      />
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
              </Accordion>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Sparkline({
  data,
  seriesColorMap,
  variableColorMap,
  colorMode,
  variableId,
}: {
  data: Record<string, any> | null;
  seriesColorMap: Record<string, string>;
  variableColorMap: Record<string, string>;
  colorMode: "byFermentation" | "byVariable";
  variableId: string;
}) {
  const chartData = React.useMemo(() => {
    if (!data || !variableId) return null;
    const online = data["fermentation-online"] as
      | { [variable: string]: { [series: string]: [number, number][] } }
      | undefined;
    return online?.[variableId] || null;
  }, [data, variableId]);

  const option = React.useMemo<EChartsOption>(() => {
    if (!chartData) {
      return {
        animation: false,
        grid: { left: 0, right: 0, top: 0, bottom: 0 },
        xAxis: { type: "value", show: false },
        yAxis: { type: "value", show: false },
        series: [],
      };
    }

    const decimate = (pts: [number, number][], target = 40) => {
      if (pts.length <= target) return pts;
      const step = Math.ceil(pts.length / target);
      const out: [number, number][] = [];
      for (let i = 0; i < pts.length; i += step) out.push(pts[i]);
      return out;
    };

    const series: any[] = [];
    Object.entries(chartData).forEach(([seriesName, points]) => {
      series.push({
        type: "line",
        name: seriesName,
        showSymbol: false,
        hoverAnimation: false,
        lineStyle: {
          width: 1,
          color:
            colorMode === "byVariable"
              ? variableColorMap[variableId] || "#888"
              : seriesColorMap[seriesName] || "#888",
        },
        data: decimate(points as [number, number][], 40),
      });
    });

    return {
      animation: false,
      grid: { left: 0, right: 0, top: 0, bottom: 0 },
      xAxis: { type: "value", show: false },
      yAxis: { type: "value", show: false },
      series,
      tooltip: { show: false },
    } as EChartsOption;
  }, [chartData, seriesColorMap, variableColorMap, colorMode, variableId]);

  return (
    <div className="w-full" style={{ height: 28 }}>
      <ReactECharts
        option={option}
        style={{ width: "100%", height: "100%" }}
        // @ts-expect-error useDirtyRect is supported by ECharts init opts (v5), typing may lag in echarts-for-react
        opts={{ renderer: "canvas", useDirtyRect: true }}
        lazyUpdate={true}
      />
    </div>
  );
}

function SectionGroup({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className={cn("font-medium", DENSE.textSm, "mb-1")}>{title}</div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function DerivedPanel({
  variables,
  pending,
  selected,
  onSelect,
  onAdd,
  onRemove,
  onChangeExpression,
  onEval,
  onEvalAll,
  knownVariableIdsForEditor,
  inspectMap,
  seriesByVar,
}: {
  variables: Record<
    string,
    {
      name: string;
      expression: string;
      error?: string | null;
    }
  >;
  pending: Record<string, boolean>;
  selected: string | null;
  onSelect: (name: string | null) => void;
  onAdd: (name: string) => void;
  onRemove: (name: string) => void;
  onChangeExpression: (name: string, expr: string) => void;
  onEval: (name: string) => void;
  onEvalAll: () => void;
  knownVariableIdsForEditor: string[];
  inspectMap: Record<string, InspectData | undefined>;
  seriesByVar: Record<string, SeriesPoints>;
}) {
  const [newName, setNewName] = React.useState("");

  const selectedVar = selected ? variables[selected] : undefined;
  const selectedError = selectedVar?.error ?? null;

  return (
    <div className="h-full flex flex-col min-h-0">
      {/* Header: Add derived */}
      <div className="p-2 border-b flex items-center gap-2">
        <Input
          placeholder="New variable name (e.g., ph_error)"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          className={cn("h-7 text-sm", "rounded-none")}
        />
        <Button
          size="sm"
          className={DENSE.chip}
          onClick={() => {
            const name = newName.trim();
            if (!name) return;
            if (variables[name]) {
              onSelect(name);
              setNewName("");
              return;
            }
            onAdd(name);
            setNewName("");
          }}
        >
          <Plus className="h-3 w-3" />
        </Button>
        <Button
          size="sm"
          variant="outline"
          className={DENSE.chip}
          onClick={() => onEvalAll()}
          title="Recompute all"
        >
          Recompute
        </Button>
      </div>

      {/* Body split: list and editor */}
      <div className="flex flex-col min-h-0 grow">
        {/* List */}
        <div className="flex-1 border-r min-h-0 overflow-auto">
          <div className="divide-y">
            {Object.values(variables).length === 0 && (
              <div className={cn("p-2", DENSE.textSm, "text-muted-foreground")}>
                No derived variables.
              </div>
            )}
            {Object.values(variables).map((dv) => {
              const hasError = !!dv.error;
              const active = selected === dv.name;
              return (
                <div
                  key={dv.name}
                  className={cn(
                    "flex items-center justify-between px-2 py-1 cursor-pointer",
                    active ? "bg-secondary" : undefined,
                  )}
                  onClick={() => onSelect(dv.name)}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    {hasError && (
                      <AlertTriangle className="h-3 w-3 text-destructive" />
                    )}
                    {pending[dv.name] && (
                      <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                    )}
                    <span
                      className={cn("truncate", DENSE.textSm)}
                      title={dv.name}
                    >
                      {dv.name}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className={DENSE.chip}
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemove(dv.name);
                      }}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <EditorPanel
          selected={selected}
          selectedError={selectedError}
          selectedVar={selectedVar}
          onChangeExpression={onChangeExpression}
          onEval={onEval}
          knownVariableIdsForEditor={knownVariableIdsForEditor}
          inspectData={selected ? inspectMap[selected] : undefined}
          seriesForSelected={selected ? seriesByVar[selected] : undefined}
          isRunning={selected ? Boolean(pending[selected]) : false}
        />
      </div>
    </div>
  );
}

function EditorPanel({
  selected,
  selectedError,
  selectedVar,
  onChangeExpression,
  onEval,
  knownVariableIdsForEditor,
  inspectData,
  seriesForSelected,
  isRunning,
}: {
  selected: string | null;
  selectedError: string | null;
  selectedVar: { name: string; expression: string } | undefined;
  onChangeExpression: (name: string, expr: string) => void;
  onEval: (name: string) => void;
  knownVariableIdsForEditor: string[];
  inspectData?: InspectData;
  seriesForSelected?: SeriesPoints;
  isRunning: boolean;
}) {
  const [docsOpen, setDocsOpen] = React.useState(false);
  const [fullscreenOpen, setFullscreenOpen] = React.useState(false);
  const [selectedSeries, setSelectedSeries] = React.useState<string | null>(
    null,
  );
  React.useEffect(() => {
    const keys = Object.keys(seriesForSelected || {});
    setSelectedSeries((prev) =>
      prev && keys.includes(prev) ? prev : keys[0] || null,
    );
  }, [seriesForSelected]);

  const routeSearch = Route.useSearch();
  const navigate = Route.useNavigate();
  const fsOpen = Boolean(routeSearch?.fs);
  const openFullscreenFor = React.useCallback(
    (name: string) => {
      navigate({
        replace: true,
        search: (prev) => ({ ...prev, fs: true, dv: name }),
      });
    },
    [navigate],
  );
  const closeFullscreen = React.useCallback(() => {
    navigate({ replace: true, search: (prev) => ({ ...prev, fs: undefined }) });
  }, [navigate]);

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      {/* Error banner */}
      {selected && selectedError && (
        <div className="m-2 border border-destructive bg-destructive/10 text-destructive rounded p-2 text-sm">
          {selectedError}
        </div>
      )}
      <div className="flex-1 min-h-0">
        {selected && selectedVar ? (
          <div className="h-full w-full flex flex-col min-h-0">
            <div className="px-2 py-1 border-b flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                className={DENSE.chip}
                onClick={() => setDocsOpen(true)}
                title="Open docs"
              >
                Docs
              </Button>
              <Button
                size="sm"
                variant="outline"
                className={DENSE.chip}
                onClick={() => selected && openFullscreenFor(selected)}
                title="Full screen editor"
              >
                Full screen
              </Button>
              <div className="ml-auto text-xs text-muted-foreground">
                {selected}
              </div>
            </div>
            <div className="flex-1 min-h-0">
              <PyExpressionEditor
                height="100%"
                value={selectedVar.expression}
                selectedName={selectedVar.name}
                onChange={(v) => onChangeExpression(selectedVar.name, v)}
                onEval={(name) => onEval(name)}
                knownVariableIds={knownVariableIdsForEditor}
                enableCopilot={true}
              />
            </div>
            <InspectorPanel
              inspectData={inspectData}
              seriesForSelected={seriesForSelected}
              selectedSeries={selectedSeries}
              onSelectSeries={setSelectedSeries}
            />
          </div>
        ) : (
          <div
            className={cn(
              "h-full w-full flex items-center justify-center",
              DENSE.textSm,
              "text-muted-foreground",
            )}
          >
            Select a variable to edit
          </div>
        )}
      </div>

      {/* Docs dialog */}
      <Dialog open={docsOpen} onOpenChange={setDocsOpen}>
        <DialogContent className="max-w-[800px]">
          <DialogHeader>
            <DialogTitle>Python environment reference</DialogTitle>
          </DialogHeader>
          <DocsPanel />
        </DialogContent>
      </Dialog>

      {/* Fullscreen editor dialog */}
      {selected && selectedVar && (
        <FullscreenEditorDialog
          open={fsOpen}
          onOpenChange={(v) =>
            v ? openFullscreenFor(selectedVar.name) : closeFullscreen()
          }
          name={selectedVar.name}
          value={selectedVar.expression}
          onChange={(v) => onChangeExpression(selectedVar.name, v)}
          onEval={() => onEval(selectedVar.name)}
          knownVariableIds={knownVariableIdsForEditor}
          inspectData={inspectData}
          seriesForSelected={seriesForSelected}
          isRunning={isRunning}
          errorMessage={selectedError || undefined}
        />
      )}
    </div>
  );
}

function InspectorPanel({
  inspectData,
  seriesForSelected,
  selectedSeries,
  onSelectSeries,
}: {
  inspectData?: InspectData;
  seriesForSelected?: SeriesPoints;
  selectedSeries: string | null;
  onSelectSeries: (s: string | null) => void;
}) {
  const seriesNames = React.useMemo(
    () => Object.keys(seriesForSelected || {}),
    [seriesForSelected],
  );
  const html = selectedSeries
    ? inspectData?.htmlBySeries?.[selectedSeries] || null
    : null;
  const repr = selectedSeries
    ? inspectData?.reprBySeries?.[selectedSeries] || inspectData?.objectRepr
    : inspectData?.objectRepr;
  const stdout = inspectData?.stdout || "";
  const table = selectedSeries
    ? inspectData?.tableBySeries?.[selectedSeries] || null
    : null;

  if (!inspectData) return null;

  return (
    <div className="border-t p-2 space-y-2">
      <div className="flex items-center gap-2">
        <div className={cn(DENSE.textSm, "text-muted-foreground")}>Series</div>
        <select
          className="h-7 text-sm border rounded px-1"
          value={selectedSeries ?? ""}
          onChange={(e) => onSelectSeries(e.target.value || null)}
        >
          {seriesNames.length === 0 && <option value="">(none)</option>}
          {seriesNames.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <div className="ml-auto text-xs text-muted-foreground">
          {inspectData.objectType}
        </div>
      </div>
      {table ? (
        <div className="max-h-48 overflow-auto border rounded">
          <table className="w-full text-xs">
            <thead>
              <tr>
                {table.columns.map((c) => (
                  <th key={c} className="text-left px-2 py-1 border-b">
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {table.rows.map((row, i) => (
                <tr key={i} className="border-b last:border-b-0">
                  {row.map((cell, j) => (
                    <td key={j} className="px-2 py-1 align-top">
                      {String(cell)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : html ? (
        <div className="max-h-48 overflow-auto border rounded">
          <HtmlContent html={html} className="p-2" />
        </div>
      ) : (
        <pre className="max-h-48 overflow-auto text-xs p-2 bg-muted rounded">
          {repr || "(no repr)"}
        </pre>
      )}
      {!!stdout && (
        <div>
          <div className={cn(DENSE.textSm, "text-muted-foreground mb-1")}>
            stdout
          </div>
          <pre className="max-h-32 overflow-auto text-xs p-2 bg-muted rounded">
            {stdout}
          </pre>
        </div>
      )}
    </div>
  );
}

function DocsPanel() {
  const [q, setQ] = React.useState("");
  const items = React.useMemo(() => DOC_ITEMS, []);
  const filtered = React.useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return items;
    return items.filter(
      (it) =>
        it.title.toLowerCase().includes(needle) ||
        (it.keywords || []).some((k) => k.toLowerCase().includes(needle)) ||
        it.body.toLowerCase().includes(needle),
    );
  }, [q, items]);
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search docs"
          className={cn("h-8 text-sm", "rounded-none")}
          inputClassName={cn("rounded-none h-8 text-sm")}
        />
      </div>
      <div className="max-h-[60vh] overflow-auto divide-y">
        {filtered.map((it, idx) => (
          <div key={idx} className="py-2">
            <div className="font-medium text-sm">{it.title}</div>
            <div className="text-sm text-muted-foreground whitespace-pre-wrap">
              {it.body}
            </div>
            {it.example && (
              <pre className="mt-1 text-xs p-2 bg-muted rounded whitespace-pre-wrap">
                {it.example}
              </pre>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

const DOC_ITEMS: {
  title: string;
  body: string;
  example?: string;
  keywords?: string[];
}[] = [
  {
    title: "Helper v(name)",
    body: "Returns a pandas DataFrame with columns t, y, series for the given variable id.",
    example:
      "df = v('temp_pv')\n# use like: df['y'].rolling(5).mean()",
    keywords: ["variables", "dataframe", "series"],
  },
  {
    title: "Expected result shape",
    body: "Expressions must return a pandas DataFrame or Series. DataFrame must include columns t, y and optionally series (defaults to 'derived').",
    example:
      "import pandas as pd\nres = v('temp_pv').assign(series='derived')",
    keywords: ["result", "normalize", "columns"],
  },
  {
    title: "Available modules",
    body: "numpy as np, pandas as pd, math, and common Python builtins are available.",
    example: "pd.concat([v('a'), v('b')])",
    keywords: ["numpy", "pandas", "math"],
  },
  {
    title: "Group operations",
    body: "Use pandas groupby('series') to compute per-fermentation calculations.",
    example: "v('temp_pv').groupby('series')['y'].mean()",
    keywords: ["groupby", "series"],
  },
  {
    title: "Time sorting",
    body: "Results are auto-sorted by t when possible.",
    example: "v('x').sort_values('t')",
  },
];

function FullscreenEditorDialog({
  open,
  onOpenChange,
  name,
  value,
  onChange,
  onEval,
  knownVariableIds,
  inspectData,
  seriesForSelected,
  isRunning,
  errorMessage,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  name: string;
  value: string;
  onChange: (v: string) => void;
  onEval: () => void;
  knownVariableIds: string[];
  inspectData?: InspectData;
  seriesForSelected?: SeriesPoints;
  isRunning: boolean;
  errorMessage?: string;
}) {
  const [showDocs, setShowDocs] = React.useState(true);
  const [paletteQ, setPaletteQ] = React.useState("");
  const [selectedSeries, setSelectedSeries] = React.useState<string | null>(
    null,
  );
  React.useEffect(() => {
    const keys = Object.keys(seriesForSelected || {});
    setSelectedSeries((prev) =>
      prev && keys.includes(prev) ? prev : keys[0] || null,
    );
  }, [seriesForSelected]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const palette = React.useMemo(() => {
    const needle = paletteQ.trim().toLowerCase();
    const all = knownVariableIds || [];
    if (!needle) return all;
    return all.filter((s) => s.toLowerCase().includes(needle));
  }, [knownVariableIds, paletteQ]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-none! max-h-none! p-0 overflow-hidden"
        onKeyDown={handleKeyDown}
        onEscapeKeyDown={(ev) => ev.preventDefault()}
      >
        <div className="w-[99vw] h-[99vh] flex">
          {/* Left palette */}
          <div className="w-64 border-r p-2 flex flex-col">
            <div className="font-medium text-sm mb-1">Variables</div>
            <Input
              value={paletteQ}
              onChange={(e) => setPaletteQ(e.target.value)}
              placeholder="Filter variables"
              className={cn("h-7 text-sm", "rounded-none")}
              inputClassName={cn("rounded-none h-7 text-sm")}
            />
            <div className="mt-2 flex-1 min-h-0 overflow-auto text-sm">
              {palette.map((id) => (
                <div key={id} className="px-1 py-0.5 border-b last:border-b-0">
                  {id}
                </div>
              ))}
            </div>
          </div>
          {/* Center editor */}
          <div className="flex-1 min-w-0 flex flex-col">
            <div className="p-2 border-b flex items-center gap-2">
              <div className="font-medium text-sm">{name}</div>
              <div className="ml-auto inline-flex items-center gap-2">
                {isRunning && (
                  <span className="inline-flex items-center gap-1 text-sm text-muted-foreground">
                    <Loader2 className="h-3 w-3 animate-spin" /> Running...
                  </span>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  className={DENSE.chip}
                  onClick={() => setShowDocs((v) => !v)}
                >
                  {showDocs ? "Hide docs" : "Show docs"}
                </Button>
                <Button
                  size="sm"
                  variant="default"
                  className={DENSE.chip}
                  onClick={() => onEval()}
                  disabled={isRunning}
                >
                  {isRunning ? "Running" : "Run"}
                </Button>
              </div>
            </div>
            {!!errorMessage && (
              <div className="m-2 border border-destructive bg-destructive/10 text-destructive rounded p-2 text-sm">
                {errorMessage}
              </div>
            )}
            <div className="flex-1 min-h-0">
              <PyExpressionEditor
                height="100%"
                value={value}
                selectedName={name}
                onChange={(v) => onChange(v)}
                onEval={() => onEval()}
                knownVariableIds={knownVariableIds}
                enableCopilot={true}
              />
            </div>
            <InspectorPanel
              inspectData={inspectData}
              seriesForSelected={seriesForSelected}
              selectedSeries={selectedSeries}
              onSelectSeries={setSelectedSeries}
            />
          </div>
          {/* Right docs */}
          {showDocs && (
            <div className="w-80 border-l p-2 overflow-auto">
              <DocsPanel />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
